import React, { useEffect, useMemo, useState } from "react";
import API from "./api";
import { IndianPhoneInput } from "./indiaPhone";
import {
  getProfileNumberDisplay,
  isProfileNoDisplaySearchMode,
  PROFILE_NO_DISPLAY_SEARCH_KEY,
} from "./profileNumber";
import { getPolicySummary, hasPermission } from "./rbac";

const DEFAULT_FILTERS = {
  q: "",
  search_by: PROFILE_NO_DISPLAY_SEARCH_KEY,
  gender_id: "",
  birth_date: "",
  birth_year_from: "",
  birth_year_to: "",
  height_cm_from: "",
  height_cm_to: "",
  address_filters: [],

  marital_status_id: "",
  caste_ids: [],
  sakha_ids: [],
  sampraday_ids: [],
  non_preferred_sakha_ids: [],
  education_category_ids: [],
  degree_names: [],
  education_status_by_id: {},
  occupation_type_ids: [],
  tag_ids: [],

  q_not_in: false,
  gender_id_not_in: false,
  birth_year_not_in: false,
  marital_status_id_not_in: false,
  caste_ids_not_in: false,
  sakha_ids_not_in: false,
  sampraday_ids_not_in: false,
  education_category_ids_not_in: false,
  occupation_type_ids_not_in: false,
  tag_ids_not_in: false,
};

const NEGATED_QUERY_SOURCE_KEYS = new Set([
  "q",
  "gender_id",
  "birth_year_from",
  "birth_year_to",
  "marital_status_id",
  "caste_ids",
  "sakha_ids",
  "sampraday_ids",
  "education_category_ids",
  "occupation_type_ids",
  "tag_ids",
]);

const GLOBAL_PREFERENCE_FILTER_DEFAULTS = {
  q: "",
  search_by: PROFILE_NO_DISPLAY_SEARCH_KEY,
  gender_id: "",
  birth_date: "",
  birth_year_from: "",
  birth_year_to: "",
  height_cm_from: "",
  height_cm_to: "",
  address_filters: [],
  marital_status_id: "",
  caste_ids: [],
  sakha_ids: [],
  sampraday_ids: [],
  non_preferred_sakha_ids: [],
  education_category_ids: [],
  degree_names: [],
  education_status_by_id: {},
  occupation_type_ids: [],
  tag_ids: [],
};
const NON_VERIFIED_FILTER_DEFAULTS = {
  q: "",
  search_by: PROFILE_NO_DISPLAY_SEARCH_KEY,
};
const PAGE_SIZE = 24;
const MIN_BIRTH_YEAR = 1900;
const SEARCH_MODE_OPTIONS = [
  { value: PROFILE_NO_DISPLAY_SEARCH_KEY, label: "Profile No" },
  { value: "name", label: "Name" },
  { value: "mobile", label: "Mobile" },
  { value: "dob", label: "DOB" },
];
const ADDRESS_TYPE_OPTIONS = [
  { value: "current", label: "Current" },
  { value: "native", label: "Native" },
  { value: "family", label: "Family" },
  { value: "office", label: "Office" },
];
const HEIGHT_RANGE_OPTIONS = buildHeightRangeOptions();

function createAddressFilter() {
  return {
    client_key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    address_type: "",
    country_ids: [],
    state_ids: [],
    district_ids: [],
  };
}

function isMobileSearchMode(value) {
  return String(value || "").trim().toLowerCase() === "mobile";
}

function isDobSearchMode(value) {
  return String(value || "").trim().toLowerCase() === "dob";
}

function normalizeSearchMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === PROFILE_NO_DISPLAY_SEARCH_KEY.toLowerCase()) return PROFILE_NO_DISPLAY_SEARCH_KEY;
  if (normalized === "name" || normalized === "mobile" || normalized === "dob") return normalized;
  return PROFILE_NO_DISPLAY_SEARCH_KEY;
}

function getSearchModeLabel(value) {
  const mode = normalizeSearchMode(value);
  return SEARCH_MODE_OPTIONS.find((option) => option.value === mode)?.label || "Search";
}

function normalizeMobileSearchValue(value) {
  return String(value || "").replace(/\D/g, "");
}

function formatSearchInputValue(mode, value) {
  const normalizedMode = normalizeSearchMode(mode);
  const rawValue = String(value || "");

  if (isDobSearchMode(normalizedMode)) return rawValue;
  if (isMobileSearchMode(normalizedMode)) return rawValue.trim();
  if (isProfileNoDisplaySearchMode(normalizedMode)) {
    return rawValue.toUpperCase().replace(/[^A-Z0-9]/g, "");
  }
  return rawValue;
}

function getSearchInputType(mode) {
  const normalizedMode = normalizeSearchMode(mode);
  if (isDobSearchMode(normalizedMode)) return "date";
  if (isMobileSearchMode(normalizedMode)) return "tel";
  return "text";
}

function getSearchInputPlaceholder(mode) {
  const normalizedMode = normalizeSearchMode(mode);
  if (isDobSearchMode(normalizedMode)) return "";
  if (isMobileSearchMode(normalizedMode)) return "Phone number";
  if (isProfileNoDisplaySearchMode(normalizedMode)) return "Enter profile number e.g. M123";
  return "Enter full name";
}

function getSearchFilterValue(filters) {
  const mode = normalizeSearchMode(filters?.search_by);
  return isDobSearchMode(mode)
    ? String(filters?.birth_date || "")
    : String(filters?.q || "");
}

function hasSearchFilterValue(filters) {
  const mode = normalizeSearchMode(filters?.search_by);
  if (isDobSearchMode(mode)) {
    return !!String(filters?.birth_date || "").trim();
  }
  if (isMobileSearchMode(mode)) {
    return normalizeMobileSearchValue(filters?.q).length > 0;
  }
  return !!String(filters?.q || "").trim();
}

function buildSearchFilterPatch(currentFilters, nextMode, rawValue) {
  const mode = normalizeSearchMode(nextMode);
  const next = {
    ...(currentFilters || {}),
    search_by: mode,
  };

  if (isDobSearchMode(mode)) {
    next.birth_date = String(rawValue || "");
    next.q = "";
    return next;
  }

  next.q = formatSearchInputValue(mode, rawValue);
  next.birth_date = "";
  return next;
}

function getQuerySearchValue(filters) {
  const mode = normalizeSearchMode(filters?.search_by);
  if (isMobileSearchMode(mode)) return normalizeMobileSearchValue(filters?.q);
  return String(filters?.q || "").trim();
}

export default function Search({
  onViewProfile,
  onViewProfileAlerts,
  onViewAssignedProfiles,
  onViewAssignedTasks,
  canViewProfile = true,
  user = null,
  selectionMode = false,
  selectedProfileIds = [],
  onToggleProfileSelection = null,
  onSelectAllVisibleProfiles = null,
  onDeselectAllVisibleProfiles = null,
  selectionCardMode = "buttons",
  heroEyebrow = "Jain Kalyanam CRM",
  heroTitle = "Discover Profiles",
  heroSubtitle = "Search, review, and organize profiles with structured filters and clear profile summaries.",
  heroToolbarExtra = null,
  afterHeroContent = null,
  showPolicyNotice = true,
  showBaseStats = true,
  showAlertStats = true,
  showAssignedProfileStats = true,
  showAssignedTaskStats = true,
  initialSplashTitle = "Profiles",
  initialSplashSubtitle = "Use filters to narrow results or load the current profile set.",
  initialSplashActionLabel = "Load Profiles",
  emptySplashTitle = "No profiles found",
  emptySplashSubtitle = "Adjust the selected filters and try again.",
}) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [nonPreferredFilters, setNonPreferredFilters] = useState(() => buildInitialNonPreferredFilters());
  const [nonVerifiedFilters, setNonVerifiedFilters] = useState(() => buildInitialNonVerifiedFilters());
  const [preferenceMode, setPreferenceMode] = useState("preferred");
  const [activeSearchMode, setActiveSearchMode] = useState("standard");
  const [results, setResults] = useState([]);
  const [rawResults, setRawResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [resultStats, setResultStats] = useState({
    male: 0,
    female: 0,
    profiles_with_alerts: 0,
    open_alerts: 0,
    renewal_alerts: 0,
    follow_up_alerts: 0,
    latest_alert_profile_id: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [assignedProfileSummary, setAssignedProfileSummary] = useState({
    total: 0,
    male: 0,
    female: 0,
    loading: true,
  });
  const [assignedTaskSummary, setAssignedTaskSummary] = useState({
    total: 0,
    pending: 0,
    in_progress: 0,
    completed: 0,
    loading: true,
  });

  const [lookups, setLookups] = useState({
    genders: [],
    locations: [],
    maritalStatus: [],
    occupationTypes: [],
    tags: [],
    castes: [],
    sakhas: [],
    sampradays: [],
    educationCategories: [],
    degreeNames: [],
  });
  const [sortBy, setSortBy] = useState("latest");
  const [viewMode, setViewMode] = useState("grid");
  const [filterOverlayOpen, setFilterOverlayOpen] = useState(false);
  const selectedProfileIdSet = useMemo(
    () => new Set(safeArray(selectedProfileIds).map((value) => String(value))),
    [selectedProfileIds]
  );

  useEffect(() => {
    loadLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchPolicy = useMemo(() => getPolicySummary(user, "profiles", "search"), [user]);
  const canViewTasks = useMemo(() => hasPermission(user, "tasks", "view"), [user]);
  const canViewAlerts = useMemo(() => hasPermission(user, "profiles", "view", "alerts"), [user]);
  const canViewCompleteness = useMemo(() => hasPermission(user, "profiles", "view", "completeness"), [user]);
  const canViewPersonaLabel = useMemo(() => {
    const hiddenFields = Array.isArray(searchPolicy?.hidden_fields)
      ? searchPolicy.hidden_fields.map((value) => String(value || "").trim().toLowerCase()).filter(Boolean)
      : [];
    return hasPermission(user, "profiles", "search", "persona_label") && !hiddenFields.includes("persona_label");
  }, [searchPolicy, user]);
  const restrictedLocations = useMemo(
    () => filterLocationsBySearchPolicy(lookups.locations, searchPolicy),
    [lookups.locations, searchPolicy]
  );
  const locationMaps = useMemo(() => buildLocationMaps(restrictedLocations), [restrictedLocations]);
  const optionMaps = useMemo(() => buildOptionMaps(lookups), [lookups]);
  const defaultFilters = useMemo(() => buildInitialFilters(lookups.maritalStatus), [lookups.maritalStatus]);
  const effectiveNonPreferredFilters = useMemo(
    () => sanitizeNonPreferredFilters(nonPreferredFilters, filters),
    [nonPreferredFilters, filters]
  );
  const heroSearchMode = normalizeSearchMode(filters.search_by);
  const heroSearchValue = getSearchFilterValue(filters);

  useEffect(() => {
    if (activeSearchMode === "non_verified") {
      setResults(rawResults);
      return;
    }
    setResults(applyLocalNotInFilters(rawResults, effectiveNonPreferredFilters, locationMaps, optionMaps));
  }, [rawResults, effectiveNonPreferredFilters, locationMaps, optionMaps, activeSearchMode]);

  useEffect(() => {
    if (!searched) return;
    search(
      activeSearchMode === "non_verified" ? nonVerifiedFilters : filters,
      false,
      1,
      sortBy,
      nonPreferredFilters,
      activeSearchMode
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  useEffect(() => {
    let active = true;

    const loadAssignedProfileSummary = async () => {
      if (!user?.id) {
        if (active) {
          setAssignedProfileSummary({
            total: 0,
            male: 0,
            female: 0,
            loading: false,
          });
        }
        return;
      }

      setAssignedProfileSummary((prev) => ({ ...prev, loading: true }));

      try {
        const res = await API.get("/profiles/assigned-to-me?include_meta=1&limit=1&offset=0");
        const payload = Array.isArray(res.data) ? { items: res.data, total: res.data.length } : (res.data || {});
        if (!active) return;
        setAssignedProfileSummary({
          total: Number(payload.total || 0),
          male: Number(payload.stats?.male || 0),
          female: Number(payload.stats?.female || 0),
          loading: false,
        });
      } catch (err) {
        console.error("Failed to load assigned profile summary", err);
        if (!active) return;
        setAssignedProfileSummary({
          total: 0,
          male: 0,
          female: 0,
          loading: false,
        });
      }
    };

    loadAssignedProfileSummary();

    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let active = true;

    const loadAssignedTaskSummary = async () => {
      if (!user?.id || !canViewTasks) {
        if (active) {
          setAssignedTaskSummary({
            total: 0,
            pending: 0,
            in_progress: 0,
            completed: 0,
            loading: false,
          });
        }
        return;
      }

      setAssignedTaskSummary((prev) => ({ ...prev, loading: true }));

      try {
        const res = await API.get("/tasks/dashboard");
        const payload = res.data || {};
        if (!active) return;
        const pending = Number(payload.my_pending || 0);
        const inProgress = Number(payload.my_in_progress || 0);
        const completed = Number(payload.my_completed || 0);
        setAssignedTaskSummary({
          total: pending + inProgress + completed,
          pending,
          in_progress: inProgress,
          completed,
          loading: false,
        });
      } catch (err) {
        console.error("Failed to load assigned task summary", err);
        if (!active) return;
        setAssignedTaskSummary({
          total: 0,
          pending: 0,
          in_progress: 0,
          completed: 0,
          loading: false,
        });
      }
    };

    loadAssignedTaskSummary();

    return () => {
      active = false;
    };
  }, [user?.id, canViewTasks]);

  const loadLookups = async () => {
    try {
      const [g, l, ms, ot, t, ca, sk, sp, ec, dn] = await Promise.all([
        API.get("/genders"),
        API.get("/locations"),
        API.get("/marital-status"),
        API.get("/occupation-types"),
        API.get("/tags"),
        API.get("/castes"),
        API.get("/sakhas"),
        API.get("/sampradays"),
        API.get("/education-categories"),
        API.get("/degree-names"),
      ]);

      setLookups({
        genders: safeArray(g.data),
        locations: safeArray(l.data),
        maritalStatus: safeArray(ms.data),
        occupationTypes: safeArray(ot.data),
        tags: safeArray(t.data),
        castes: safeArray(ca.data),
        sakhas: safeArray(sk.data),
        sampradays: safeArray(sp.data),
        educationCategories: safeArray(ec.data),
        degreeNames: safeArray(dn.data),
      });

      const initialFilters = buildInitialFilters(ms.data);
      const initialNonPreferredFilters = buildInitialNonPreferredFilters();
      const initialNonVerifiedFilters = buildInitialNonVerifiedFilters();
      setFilters(initialFilters);
      setNonPreferredFilters(initialNonPreferredFilters);
      setNonVerifiedFilters(initialNonVerifiedFilters);
      setActiveSearchMode("standard");
      search(initialFilters, true, 1, sortBy, initialNonPreferredFilters, "standard");
    } catch (err) {
      console.error("Failed to load filters", err);
      setError("Failed to load search filters.");
    }
  };

  const set = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "caste_ids") {
        next.sakha_ids = [];
        next.non_preferred_sakha_ids = [];
      }

      return next;
    });
  };

  const setGlobalPreferenceFilter = (key, value) => {
    setNonPreferredFilters((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "caste_ids") {
        next.sakha_ids = [];
        next.non_preferred_sakha_ids = [];
      }

      return next;
    });
  };

  const setNonVerifiedFilter = (key, value) => {
    setNonVerifiedFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setHeroSearchMode = (nextMode) => {
    setFilters((prev) => buildSearchFilterPatch(prev, nextMode, ""));
  };

  const setHeroSearchValue = (nextValue) => {
    setFilters((prev) => buildSearchFilterPatch(prev, prev.search_by, nextValue));
  };

  const toggleArrayValue = (key, value) => {
    const num = Number(value);
    if (!num) return;

    setFilters((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      const nextValue = current.includes(num)
        ? current.filter((item) => item !== num)
        : [...current, num];
      const next = {
        ...prev,
        [key]: nextValue,
      };

      if (key === "caste_ids") {
        next.sakha_ids = [];
        next.non_preferred_sakha_ids = [];
      }

      if (key === "education_category_ids") {
        const nextStatuses = { ...(prev.education_status_by_id || {}) };
        Object.keys(nextStatuses).forEach((statusKey) => {
          if (!nextValue.includes(Number(statusKey))) delete nextStatuses[statusKey];
        });
        next.education_status_by_id = nextStatuses;
        next.degree_names = filterDegreeNamesByEducationSelection(prev.degree_names, lookups.degreeNames, nextValue);
      }

      return next;
    });
  };

  const toggleArrayTextValue = (key, value) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return;

    setFilters((prev) => {
      const current = safeArray(prev[key]).map((item) => String(item || "").trim()).filter(Boolean);
      const nextValue = current.includes(normalizedValue)
        ? current.filter((item) => item !== normalizedValue)
        : [...current, normalizedValue];

      return {
        ...prev,
        [key]: nextValue,
      };
    });
  };

  const toggleGlobalPreferenceArrayTextValue = (key, value) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return;

    setNonPreferredFilters((prev) => {
      const current = safeArray(prev[key]).map((item) => String(item || "").trim()).filter(Boolean);
      const nextValue = current.includes(normalizedValue)
        ? current.filter((item) => item !== normalizedValue)
        : [...current, normalizedValue];

      return {
        ...prev,
        [key]: nextValue,
      };
    });
  };

  const toggleGlobalPreferenceArrayValue = (key, value) => {
    const num = Number(value);
    if (!num) return;

    setNonPreferredFilters((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      const nextValue = current.includes(num)
        ? current.filter((item) => item !== num)
        : [...current, num];
      const next = {
        ...prev,
        [key]: nextValue,
      };

      if (key === "caste_ids") {
        next.sakha_ids = [];
        next.non_preferred_sakha_ids = [];
      }

      if (key === "education_category_ids") {
        const nextStatuses = { ...(prev.education_status_by_id || {}) };
        Object.keys(nextStatuses).forEach((statusKey) => {
          if (!nextValue.includes(Number(statusKey))) delete nextStatuses[statusKey];
        });
        next.education_status_by_id = nextStatuses;
        next.degree_names = filterDegreeNamesByEducationSelection(prev.degree_names, lookups.degreeNames, nextValue);
      }

      return next;
    });
  };

  const toggleEducationPursuingForId = (educationId) => {
    const id = Number(educationId);
    if (!id) return;
    setFilters((prev) => {
      const current = normalizeEducationStatusValue(prev.education_status_by_id?.[id]) || "completed";
      return {
        ...prev,
        education_status_by_id: {
          ...(prev.education_status_by_id || {}),
          [id]: current === "pursuing" ? "completed" : "pursuing",
        },
      };
    });
  };

  const toggleGlobalPreferencePursuingForId = (educationId) => {
    const id = Number(educationId);
    if (!id) return;
    setNonPreferredFilters((prev) => {
      const current = normalizeEducationStatusValue(prev.education_status_by_id?.[id]) || "completed";
      return {
        ...prev,
        education_status_by_id: {
          ...(prev.education_status_by_id || {}),
          [id]: current === "pursuing" ? "completed" : "pursuing",
        },
      };
    });
  };

  const addAddressFilter = () => {
    setFilters((prev) => ({
      ...prev,
      address_filters: [...safeArray(prev.address_filters), createAddressFilter()],
    }));
  };

  const addGlobalPreferenceAddressFilter = () => {
    setNonPreferredFilters((prev) => ({
      ...prev,
      address_filters: [...safeArray(prev.address_filters), createAddressFilter()],
    }));
  };

  const removeAddressFilter = (clientKey) => {
    setFilters((prev) => ({
      ...prev,
      address_filters: safeArray(prev.address_filters).filter((filter) => filter.client_key !== clientKey),
    }));
  };

  const removeGlobalPreferenceAddressFilter = (clientKey) => {
    setNonPreferredFilters((prev) => ({
      ...prev,
      address_filters: safeArray(prev.address_filters).filter((filter) => filter.client_key !== clientKey),
    }));
  };

  const setAddressFilterType = (clientKey, value) => {
    setFilters((prev) => ({
      ...prev,
      address_filters: safeArray(prev.address_filters).map((filter) =>
        filter.client_key !== clientKey
          ? filter
          : {
              ...filter,
              address_type: value,
              country_ids: [],
              state_ids: [],
              district_ids: [],
            }
      ),
    }));
  };

  const setGlobalPreferenceAddressFilterType = (clientKey, value) => {
    setNonPreferredFilters((prev) => ({
      ...prev,
      address_filters: safeArray(prev.address_filters).map((filter) =>
        filter.client_key !== clientKey
          ? filter
          : {
              ...filter,
              address_type: value,
              country_ids: [],
              state_ids: [],
              district_ids: [],
            }
      ),
    }));
  };

  const toggleAddressFilterArrayValue = (clientKey, key, value) => {
    const num = Number(value);
    if (!num) return;

    setFilters((prev) => ({
      ...prev,
      address_filters: safeArray(prev.address_filters).map((filter) => {
        if (filter.client_key !== clientKey) return filter;

        const current = safeArray(filter[key]).map(Number).filter(Boolean);
        const nextValues = current.includes(num)
          ? current.filter((item) => item !== num)
          : [...current, num];

        const nextFilter = {
          ...filter,
          [key]: nextValues,
        };

        if (key === "country_ids") {
          nextFilter.state_ids = [];
          nextFilter.district_ids = [];
        }

        if (key === "state_ids") {
          nextFilter.district_ids = [];
        }

        return nextFilter;
      }),
    }));
  };

  const toggleGlobalPreferenceAddressFilterArrayValue = (clientKey, key, value) => {
    const num = Number(value);
    if (!num) return;

    setNonPreferredFilters((prev) => ({
      ...prev,
      address_filters: safeArray(prev.address_filters).map((filter) => {
        if (filter.client_key !== clientKey) return filter;

        const current = safeArray(filter[key]).map(Number).filter(Boolean);
        const nextValues = current.includes(num)
          ? current.filter((item) => item !== num)
          : [...current, num];

        const nextFilter = {
          ...filter,
          [key]: nextValues,
        };

        if (key === "country_ids") {
          nextFilter.state_ids = [];
          nextFilter.district_ids = [];
        }

        if (key === "state_ids") {
          nextFilter.district_ids = [];
        }

        return nextFilter;
      }),
    }));
  };

  const buildQuery = (sourceFilters = filters, page = 1, sortValue = sortBy, mode = "standard") => {
    const clean = {};
    if (mode === "non_verified") {
      const queryText = getQuerySearchValue(sourceFilters);
      const searchMode = normalizeSearchMode(sourceFilters?.search_by);
      if (queryText) {
        clean.q = queryText;
        clean.search_by = isProfileNoDisplaySearchMode(searchMode)
          ? PROFILE_NO_DISPLAY_SEARCH_KEY
          : isMobileSearchMode(searchMode)
            ? "mobile"
            : "name";
      }
      if (isDobSearchMode(searchMode) && sourceFilters?.birth_date) {
        clean.birth_date = sourceFilters.birth_date;
      }
      clean.verification_status = "unverified";
    } else {
      const educationPairFilters = getEducationPairFilters(sourceFilters);
      const addressFilters = getActiveAddressFilters(sourceFilters.address_filters);
      const hasDegreeNames = safeArray(sourceFilters.degree_names).length > 0;

      Object.entries(sourceFilters).forEach(([key, value]) => {
        if (
          key.endsWith("_not_in") ||
          key === "country" ||
          key === "education_status_by_id" ||
          key === "address_filters" ||
          key === "non_preferred_sakha_ids" ||
          key === "search_by" ||
          key === "height_cm_from" ||
          key === "height_cm_to"
        ) return;

        if (hasDegreeNames && key === "education_category_ids") return;

        const isBirthYearKey = key === "birth_year_from" || key === "birth_year_to";
        const notInKey = isBirthYearKey ? "birth_year_not_in" : `${key}_not_in`;

        if (NEGATED_QUERY_SOURCE_KEYS.has(key) && sourceFilters[notInKey]) return;

        if (Array.isArray(value)) {
          if (value.length) clean[key] = value.join(",");
          return;
        }

        if (value !== "" && value !== null && value !== undefined && value !== false) {
          clean[key] = value;
        }
      });

      const queryText = getQuerySearchValue(sourceFilters);
      const searchMode = normalizeSearchMode(sourceFilters.search_by);

      if (queryText) {
        clean.q = queryText;
        clean.search_by = isProfileNoDisplaySearchMode(searchMode)
          ? PROFILE_NO_DISPLAY_SEARCH_KEY
          : isMobileSearchMode(searchMode)
            ? "mobile"
            : "name";
      }

      const normalizedHeightRange = getNormalizedHeightRange(sourceFilters);
      if (normalizedHeightRange.from) clean.height_cm_from = normalizedHeightRange.from;
      if (normalizedHeightRange.to) clean.height_cm_to = normalizedHeightRange.to;

      if (educationPairFilters.length) {
        clean.education_pair_filters = educationPairFilters
          .map((item) => `${item.education_category_id}:${item.education_status}`)
          .join(",");
      }

      if (addressFilters.length) {
        clean.address_filters = JSON.stringify(addressFilters);
      }
    }

    clean.limit = PAGE_SIZE;
    clean.offset = Math.max(0, (page - 1) * PAGE_SIZE);
    clean.include_meta = 1;

    if (sortValue === "oldest") {
      clean.sort_by = "created_at";
      clean.sort_dir = "asc";
    } else if (sortValue === "profile_no_asc") {
      clean.sort_by = "profile_no_display";
      clean.sort_dir = "asc";
    } else if (sortValue === "profile_no_desc") {
      clean.sort_by = "profile_no_display";
      clean.sort_dir = "desc";
    } else if (sortValue === "name") {
      clean.sort_by = "full_name";
      clean.sort_dir = "asc";
    } else if (sortValue === "age_asc") {
      clean.sort_by = "age";
      clean.sort_dir = "asc";
    } else if (sortValue === "age_desc") {
      clean.sort_by = "age";
      clean.sort_dir = "desc";
    } else {
      clean.sort_by = "created_at";
      clean.sort_dir = "desc";
    }

    return new URLSearchParams(clean).toString();
  };

  const search = async (
    sourceFilters = filters,
    initial = false,
    page = 1,
    sortValue = sortBy,
    sourceNonPreferredFilters = nonPreferredFilters,
    mode = "standard"
  ) => {
    const nextMode = mode === "non_verified" ? "non_verified" : "standard";
    const hasSearchValue = hasSearchFilterValue(sourceFilters);

    if (nextMode === "non_verified" && !hasSearchValue) {
      setActiveSearchMode("non_verified");
      setResults([]);
      setRawResults([]);
      setTotalResults(0);
      setCurrentPage(1);
      setResultStats({
        male: 0,
        female: 0,
        profiles_with_alerts: 0,
        open_alerts: 0,
        renewal_alerts: 0,
        follow_up_alerts: 0,
        latest_alert_profile_id: null,
      });
      setSearched(false);
      setError("Search by profile number, name, mobile, or DOB to view non verified profiles.");
      return;
    }

    setActiveSearchMode(nextMode);
    setLoading(true);
    setSearched(true);
    setError("");

    try {
      const query = buildQuery(sourceFilters, page, sortValue, nextMode);
      const res = await API.get(`/profiles/search${query ? `?${query}` : ""}`);
      const payload = Array.isArray(res.data) ? { items: res.data, total: res.data.length } : (res.data || {});
      const raw = safeArray(payload.items);
      const nextTotal = Number.isFinite(Number(payload.total)) ? Number(payload.total) : raw.length;
      const nextTotalPages = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE));

      if (page > nextTotalPages) {
        await search(sourceFilters, initial, nextTotalPages, sortValue, sourceNonPreferredFilters, nextMode);
        return;
      }

      setCurrentPage(page);
      setTotalResults(nextTotal);
      setResultStats({
        male: Number(payload.stats?.male ?? raw.filter((p) => String(p.gender || "").toLowerCase() === "male").length),
        female: Number(payload.stats?.female ?? raw.filter((p) => String(p.gender || "").toLowerCase() === "female").length),
        profiles_with_alerts: Number(payload.stats?.profiles_with_alerts || 0),
        open_alerts: Number(payload.stats?.open_alerts || 0),
        renewal_alerts: Number(payload.stats?.renewal_alerts || 0),
        follow_up_alerts: Number(payload.stats?.follow_up_alerts || 0),
        latest_alert_profile_id: payload.stats?.latest_alert_profile_id || null,
      });
      setRawResults(raw);
      setResults(
        nextMode === "non_verified"
          ? raw
          : applyLocalNotInFilters(
              raw,
              sanitizeNonPreferredFilters(sourceNonPreferredFilters, sourceFilters),
              locationMaps,
              optionMaps
            )
      );
    } catch (err) {
      console.error("Search failed", err);
      setError(err.response?.data?.error || "Search failed. Please check the server.");
      if (!initial) alert("Search failed. Please check the server.");
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    const resetFilters = buildInitialFilters(lookups.maritalStatus);
    const resetNonPreferredFilters = buildInitialNonPreferredFilters();
    const resetNonVerifiedFilters = buildInitialNonVerifiedFilters();
    setFilters(resetFilters);
    setNonPreferredFilters(resetNonPreferredFilters);
    setNonVerifiedFilters(resetNonVerifiedFilters);
    setPreferenceMode("preferred");
    setActiveSearchMode("standard");
    setResults([]);
    setRawResults([]);
    setTotalResults(0);
    setResultStats({
      male: 0,
      female: 0,
      profiles_with_alerts: 0,
      open_alerts: 0,
      renewal_alerts: 0,
      follow_up_alerts: 0,
      latest_alert_profile_id: null,
    });
    setCurrentPage(1);
    setSearched(false);
    setError("");
  };

  const handleHeroSearchSubmit = (event) => {
    event?.preventDefault?.();
    setPreferenceMode("preferred");
    search(filters, false, 1, sortBy, nonPreferredFilters, "standard");
  };

  const filteredSakhas = useMemo(() => {
    const selectedCasteIds = safeArray(filters.caste_ids).map(Number).filter(Boolean);
    if (!selectedCasteIds.length) return lookups.sakhas;
    return lookups.sakhas.filter((s) => selectedCasteIds.includes(Number(s.caste_id)));
  }, [lookups.sakhas, filters.caste_ids]);

  const filteredGlobalPreferenceSakhas = useMemo(() => {
    const selectedCasteIds = safeArray(nonPreferredFilters.caste_ids).map(Number).filter(Boolean);
    if (!selectedCasteIds.length) return lookups.sakhas;
    return lookups.sakhas.filter((s) => selectedCasteIds.includes(Number(s.caste_id)));
  }, [lookups.sakhas, nonPreferredFilters.caste_ids]);

  const hasDegreeCategoryMapping = useMemo(
    () => safeArray(lookups.degreeNames).some((item) => Number(item?.education_category_id) > 0),
    [lookups.degreeNames]
  );

  const filteredDegreeNames = useMemo(() => {
    if (!hasDegreeCategoryMapping) return lookups.degreeNames;
    const selectedEducationIds = safeArray(filters.education_category_ids).map(Number).filter(Boolean);
    if (!selectedEducationIds.length) return [];
    return safeArray(lookups.degreeNames).filter((item) =>
      selectedEducationIds.includes(Number(item?.education_category_id))
    );
  }, [filters.education_category_ids, hasDegreeCategoryMapping, lookups.degreeNames]);

  const filteredGlobalPreferenceDegreeNames = useMemo(() => {
    if (!hasDegreeCategoryMapping) return lookups.degreeNames;
    const selectedEducationIds = safeArray(nonPreferredFilters.education_category_ids).map(Number).filter(Boolean);
    if (!selectedEducationIds.length) return [];
    return safeArray(lookups.degreeNames).filter((item) =>
      selectedEducationIds.includes(Number(item?.education_category_id))
    );
  }, [nonPreferredFilters.education_category_ids, hasDegreeCategoryMapping, lookups.degreeNames]);

  const sortedResults = useMemo(() => {
    return results;
  }, [results]);

  const totalPages = Math.max(1, Math.ceil(totalResults / PAGE_SIZE));

  const activeFilterCount = useMemo(() => {
    if (activeSearchMode === "non_verified") {
      return countNonVerifiedFilters(nonVerifiedFilters);
    }
    return (
      countAppliedFilters(filters, String(defaultFilters.marital_status_id || "")) +
      countAppliedFilters(effectiveNonPreferredFilters)
    );
  }, [filters, effectiveNonPreferredFilters, nonVerifiedFilters, defaultFilters, activeSearchMode]);

  const runSearchForCurrentTab = () => {
    if (preferenceMode === "non_verified") {
      return search(nonVerifiedFilters, false, 1, sortBy, nonPreferredFilters, "non_verified");
    }
    return search(filters, false, 1, sortBy, nonPreferredFilters, "standard");
  };

  const handleRemoveActiveFilterChip = (chip) => {
    if (!chip?.source || !chip?.key) return;

    if (chip.source === "nonVerifiedFilters") {
      const nextNonVerifiedFilters =
        chip.key === "q"
          ? {
              ...nonVerifiedFilters,
              q: "",
            }
          : chip.key === "birth_date"
            ? {
                ...nonVerifiedFilters,
                birth_date: "",
              }
          : nonVerifiedFilters;
      setNonVerifiedFilters(nextNonVerifiedFilters);
      search(nextNonVerifiedFilters, false, 1, sortBy, nonPreferredFilters, "non_verified");
      return;
    }

    if (chip.source === "nonPreferredFilters") {
      const nextNonPreferredFilters = buildNextFiltersAfterChipRemoval(
        nonPreferredFilters,
        chip,
        "",
        lookups.degreeNames
      );
      setNonPreferredFilters(nextNonPreferredFilters);
      search(filters, false, 1, sortBy, nextNonPreferredFilters, "standard");
      return;
    }

    const nextFilters = buildNextFiltersAfterChipRemoval(
      filters,
      chip,
      String(defaultFilters.marital_status_id || ""),
      lookups.degreeNames
    );
    setFilters(nextFilters);
    search(nextFilters, false, 1, sortBy, nonPreferredFilters, "standard");
  };

  const stats = useMemo(() => {
    const total = totalResults;
    const male = resultStats.male;
    const female = resultStats.female;
    const profilesWithAlerts = resultStats.profiles_with_alerts;
    const openAlerts = resultStats.open_alerts;
    const renewalAlerts = resultStats.renewal_alerts;
    const followUpAlerts = resultStats.follow_up_alerts;
    const latestAlertProfileId = resultStats.latest_alert_profile_id || null;
    return { total, male, female, profilesWithAlerts, openAlerts, renewalAlerts, followUpAlerts, latestAlertProfileId };
  }, [resultStats, totalResults]);

  const hasSearchPolicyNotice = Boolean(
    searchPolicy?.max_results ||
      (Array.isArray(searchPolicy?.allowed_city_ids) && searchPolicy.allowed_city_ids.length) ||
      (Array.isArray(searchPolicy?.allowed_state_ids) && searchPolicy.allowed_state_ids.length)
  );

  return (
    <div style={S.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { transform: scale(1); opacity: .62; } 50% { transform: scale(1.08); opacity: 1; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes overlayFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes drawerUp { from { opacity: 0; transform: translateY(24px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        details summary::-webkit-details-marker { display: none; }
        @media (max-width: 980px) {
          .jk-search-layout { display: block !important; }
          .jk-search-main { width: 100% !important; }
        }
        @media (max-width: 720px) {
          .jk-search-hero { flex-direction: column !important; align-items: flex-start !important; }
          .jk-stat-grid { grid-template-columns: 1fr !important; }
          .jk-filter-grid { grid-template-columns: 1fr !important; }
          .jk-address-filter-grid { grid-template-columns: 1fr !important; }
          .jk-height-range-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={S.layout} className="jk-search-layout">
        <main style={S.main} className="jk-search-main">
          <div style={S.hero} className="jk-search-hero">
            <div>
              <form style={S.heroSearchForm} onSubmit={handleHeroSearchSubmit} aria-label={heroSubtitle}>
                <div style={S.heroSearchRow}>
                  {isMobileSearchMode(heroSearchMode) ? (
                    <IndianPhoneInput
                      value={heroSearchValue}
                      onChange={setHeroSearchValue}
                      wrapperStyle={S.heroPhoneInputWrap}
                      selectWrapStyle={S.heroPhoneSelectWrap}
                      selectStyle={S.heroPhoneSelect}
                      inputStyle={S.heroSearchInput}
                      placeholder={getSearchInputPlaceholder(heroSearchMode)}
                    />
                  ) : (
                    <input
                      type={getSearchInputType(heroSearchMode)}
                      style={S.heroSearchInput}
                      value={heroSearchValue}
                      onChange={(e) => setHeroSearchValue(e.target.value)}
                      placeholder={getSearchInputPlaceholder(heroSearchMode)}
                    />
                  )}
                  <button type="submit" style={S.heroSearchBtn}>
                    Search
                  </button>
                </div>
                <div style={S.heroSearchModes}>
                  {SEARCH_MODE_OPTIONS.map((option) => (
                    <label key={option.value} style={S.heroSearchModeOption}>
                      <input
                        type="radio"
                        name="hero_search_mode"
                        value={option.value}
                        checked={heroSearchMode === option.value}
                        onChange={(e) => setHeroSearchMode(e.target.value)}
                        style={S.radioInput}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </form>
            </div>

            <div style={S.toolbar}>
              <div style={S.toolbarGroup}>
                <span style={S.toolbarLabel}>Sort by</span>
                <select style={S.toolbarSelect} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="latest">Newest Profile First</option>
                  <option value="oldest">Oldest Profile First</option>
                  <option value="profile_no_asc">Profile No Ascending</option>
                  <option value="profile_no_desc">Profile No Descending</option>
                  <option value="name">Name A-Z</option>
                  <option value="age_asc">Age Low-High</option>
                  <option value="age_desc">Age High-Low</option>
                </select>
              </div>

              <div style={S.toolbarDivider} />

              <div style={S.toolbarGroup}>
                <span style={S.toolbarLabel}>View</span>
                <div style={S.viewToggle}>
                  <button style={{ ...S.viewToggleBtn, ...(viewMode === "grid" ? S.viewToggleActive : {}) }} onClick={() => setViewMode("grid")}>Grid</button>
                  <button style={{ ...S.viewToggleBtn, ...(viewMode === "list" ? S.viewToggleActive : {}) }} onClick={() => setViewMode("list")}>List</button>
                </div>
              </div>
              {selectionMode && selectionCardMode === "checkbox" && (
                <>
                  <div style={S.toolbarDivider} />
                  <div style={S.toolbarGroup}>
                    <button
                      type="button"
                      style={S.toolbarGhostBtn}
                      onClick={() => {
                        if (typeof onSelectAllVisibleProfiles === "function") {
                          onSelectAllVisibleProfiles(sortedResults);
                        }
                      }}
                      disabled={sortedResults.length === 0}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      style={S.toolbarGhostBtn}
                      onClick={() => {
                        if (typeof onDeselectAllVisibleProfiles === "function") {
                          onDeselectAllVisibleProfiles(sortedResults);
                        }
                      }}
                      disabled={selectedProfileIdSet.size === 0}
                    >
                      Deselect All
                    </button>
                  </div>
                </>
              )}
              {heroToolbarExtra}
            </div>
          </div>

          {typeof afterHeroContent === "function" ? afterHeroContent() : afterHeroContent}

          {showPolicyNotice && hasSearchPolicyNotice && (
            <div style={S.policyNotice}>
              <strong style={S.policyNoticeTitle}>Access policy applied.</strong>
              <span>
                {[
                  searchPolicy?.max_results ? `Max ${searchPolicy.max_results} results` : "",
                  searchPolicy?.allowed_state_ids?.length ? `${searchPolicy.allowed_state_ids.length} allowed states` : "",
                  searchPolicy?.allowed_city_ids?.length ? `${searchPolicy.allowed_city_ids.length} allowed cities` : "",
                ]
                  .filter(Boolean)
                  .join(" • ")}
              </span>
            </div>
          )}

          {(showBaseStats || (showAlertStats && canViewAlerts) || showAssignedProfileStats || (showAssignedTaskStats && canViewTasks)) && (
            <div style={S.statGrid} className="jk-stat-grid">
              {showBaseStats && (
                <>
                  <StatCard label="Results" value={stats.total} icon={<ResultsIcon size={22} color={C.accent} />} />
                  <StatCard label="Male" value={stats.male} icon={<MaleIcon size={22} color={C.accent} />} />
                  <StatCard label="Female" value={stats.female} icon={<FemaleIcon size={22} color={C.accent} />} />
                </>
              )}
              {showAlertStats && canViewAlerts && (
                <StatCard
                  label="Renewal Alerts"
                  value={stats.renewalAlerts}
                  icon={<AlertSummaryIcon size={22} color={C.accent} />}
                  hint={
                    stats.renewalAlerts > 0
                      ? stats.followUpAlerts > 0
                        ? `Open payment alerts · ${stats.followUpAlerts} follow-up alerts also active`
                        : "Open payment alert list"
                      : stats.followUpAlerts > 0
                        ? `${stats.followUpAlerts} follow-up alerts active`
                        : "No open renewal alerts"
                  }
                  onClick={
                    typeof onViewProfileAlerts === "function"
                      ? () => onViewProfileAlerts({ alertType: "payment" })
                      : undefined
                  }
                />
              )}
              {showAssignedProfileStats && (
                <StatCard
                  label="Profiles Allocated to Me"
                  value={assignedProfileSummary.loading ? "..." : assignedProfileSummary.total}
                  icon={<AssignedProfilesIcon size={22} color={C.accent} />}
                  hint="Open assigned profiles"
                  onClick={typeof onViewAssignedProfiles === "function" ? onViewAssignedProfiles : undefined}
                />
              )}
              {showAssignedTaskStats && canViewTasks && (
                <StatCard
                  label="Tasks Assigned to Me"
                  value={assignedTaskSummary.loading ? "..." : assignedTaskSummary.total}
                  icon={<AssignedTasksIcon size={22} color={C.accent} />}
                  hint="Open task queue"
                  onClick={typeof onViewAssignedTasks === "function" ? onViewAssignedTasks : undefined}
                />
              )}
            </div>
          )}

          <ActiveFilterBar
            activeSearchMode={activeSearchMode}
            filters={filters}
            nonPreferredFilters={effectiveNonPreferredFilters}
            nonVerifiedFilters={nonVerifiedFilters}
            lookups={lookups}
            locationMaps={locationMaps}
            defaultFilters={defaultFilters}
            activeFilterCount={activeFilterCount}
            onOpenFilters={() => setFilterOverlayOpen(true)}
            onRemoveChip={handleRemoveActiveFilterChip}
            onClear={clear}
          />

          {error && <div style={S.errorBox}>{error}</div>}

          {!searched && !loading && (
            <Splash
              title={initialSplashTitle}
              subtitle={initialSplashSubtitle}
              action={<button style={S.splashClear} onClick={() => search(filters, false, 1, sortBy, nonPreferredFilters, "standard")}>{initialSplashActionLabel}</button>}
            />
          )}

          {loading && (
            <div style={S.splash}>
              <div style={S.spinner} />
              <p style={S.splashSub}>Searching profiles...</p>
            </div>
          )}

          {searched && !loading && sortedResults.length === 0 && (
            <Splash title={emptySplashTitle} subtitle={emptySplashSubtitle} action={<button style={S.splashClear} onClick={clear}>Clear Filters</button>} />
          )}

          {!loading && sortedResults.length > 0 && (
            <>
              <div style={viewMode === "grid" ? S.grid : S.list}>
                {sortedResults.map((profile) => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    locationMaps={locationMaps}
                    mode={viewMode}
                    selectionMode={selectionMode}
                    selectionCardMode={selectionCardMode}
                    canViewPersonaLabel={canViewPersonaLabel}
                    canViewCompleteness={canViewCompleteness}
                    canViewProfile={selectionMode || canViewProfile}
                    actionActive={selectionMode && selectedProfileIdSet.has(String(profile.id))}
                    actionLabel={
                      selectionMode && selectionCardMode === "checkbox"
                        ? "View"
                        : selectionMode
                          ? "Select"
                          : "View Profile"
                    }
                    actionActiveLabel={
                      selectionMode && selectionCardMode === "checkbox"
                        ? "View"
                        : selectionMode
                          ? "Selected"
                          : "View Profile"
                    }
                    showSecondaryAction={selectionMode && selectionCardMode !== "checkbox"}
                    secondaryActionLabel="View"
                    onSecondaryAction={
                      selectionMode && selectionCardMode !== "checkbox" && typeof onViewProfile === "function"
                        ? () => onViewProfile(profile.id)
                        : null
                    }
                    onToggleSelection={
                      selectionMode && typeof onToggleProfileSelection === "function"
                        ? () => onToggleProfileSelection(profile)
                        : null
                    }
                    onView={() => {
                      if (selectionMode && selectionCardMode !== "checkbox" && typeof onToggleProfileSelection === "function") {
                        onToggleProfileSelection(profile);
                      } else if (typeof onViewProfile === "function") {
                        onViewProfile(profile.id);
                      } else {
                        alert("Navigation not wired. Check App.js");
                      }
                    }}
                  />
                ))}
              </div>

              <div style={S.paginationBar}>
                <button
                  type="button"
                  style={{ ...S.paginationBtn, ...(currentPage <= 1 ? S.paginationBtnDisabled : {}) }}
                  disabled={currentPage <= 1 || loading}
                  onClick={() =>
                    search(
                      activeSearchMode === "non_verified" ? nonVerifiedFilters : filters,
                      false,
                      currentPage - 1,
                      sortBy,
                      nonPreferredFilters,
                      activeSearchMode
                    )
                  }
                >
                  ← Previous
                </button>

                <span style={S.paginationInfo}>
                  Page {currentPage} of {totalPages} | {totalResults} profiles
                </span>

                <button
                  type="button"
                  style={{ ...S.paginationBtn, ...(currentPage >= totalPages ? S.paginationBtnDisabled : {}) }}
                  disabled={currentPage >= totalPages || loading}
                  onClick={() =>
                    search(
                      activeSearchMode === "non_verified" ? nonVerifiedFilters : filters,
                      false,
                      currentPage + 1,
                      sortBy,
                      nonPreferredFilters,
                      activeSearchMode
                    )
                  }
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </main>
      </div>

      {filterOverlayOpen && (
        <div
          style={S.filterOverlayBackdrop}
          onClick={(event) => {
            if (event.target === event.currentTarget) setFilterOverlayOpen(false);
          }}
        >
          <div style={S.filterOverlayCard}>
            <FilterPanel
              filters={filters}
              nonPreferredFilters={nonPreferredFilters}
              nonVerifiedFilters={nonVerifiedFilters}
              preferenceMode={preferenceMode}
              setPreferenceMode={setPreferenceMode}
              set={set}
              setGlobalPreferenceFilter={setGlobalPreferenceFilter}
              setNonVerifiedFilter={setNonVerifiedFilter}
              toggleArrayValue={toggleArrayValue}
              toggleGlobalPreferenceArrayValue={toggleGlobalPreferenceArrayValue}
              toggleArrayTextValue={toggleArrayTextValue}
              toggleGlobalPreferenceArrayTextValue={toggleGlobalPreferenceArrayTextValue}
              toggleEducationPursuingForId={toggleEducationPursuingForId}
              toggleGlobalPreferencePursuingForId={toggleGlobalPreferencePursuingForId}
              addAddressFilter={addAddressFilter}
              addGlobalPreferenceAddressFilter={addGlobalPreferenceAddressFilter}
              removeAddressFilter={removeAddressFilter}
              removeGlobalPreferenceAddressFilter={removeGlobalPreferenceAddressFilter}
              setAddressFilterType={setAddressFilterType}
              setGlobalPreferenceAddressFilterType={setGlobalPreferenceAddressFilterType}
              toggleAddressFilterArrayValue={toggleAddressFilterArrayValue}
              toggleGlobalPreferenceAddressFilterArrayValue={toggleGlobalPreferenceAddressFilterArrayValue}
              lookups={{ ...lookups, locations: restrictedLocations }}
              filteredSakhas={filteredSakhas}
              filteredGlobalPreferenceSakhas={filteredGlobalPreferenceSakhas}
              filteredDegreeNames={filteredDegreeNames}
              filteredGlobalPreferenceDegreeNames={filteredGlobalPreferenceDegreeNames}
              hasDegreeCategoryMapping={hasDegreeCategoryMapping}
              search={runSearchForCurrentTab}
              clear={clear}
              loading={loading}
              activeFilterCount={activeFilterCount}
              resultsCount={totalResults}
              onClose={() => setFilterOverlayOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}


function ActiveFilterBar({
  activeSearchMode,
  filters,
  nonPreferredFilters,
  nonVerifiedFilters,
  lookups,
  locationMaps,
  defaultFilters,
  activeFilterCount,
  onOpenFilters,
  onRemoveChip,
  onClear,
}) {
  const chips = [];

  if (activeSearchMode === "non_verified") {
    pushNonVerifiedChips(chips, nonVerifiedFilters);
  } else {
    pushPreferenceChips({
      chips,
      filters,
      lookups,
      locationMaps,
      defaultMaritalStatusId: String(defaultFilters?.marital_status_id || ""),
      source: "filters",
      notIn: false,
    });
    pushPreferenceChips({
      chips,
      filters: nonPreferredFilters,
      lookups,
      locationMaps,
      defaultMaritalStatusId: "",
      source: "nonPreferredFilters",
      notIn: true,
    });
  }

  return (
    <div style={S.activeFilterBar}>
      <div style={S.activeFilterContent}>
        <div style={S.activeFilterHeader}>
          <p style={S.activeFilterTitle}>Active Filters</p>
          <div style={S.activeFilterActions}>
            <button type="button" style={S.filterInlineBtn} onClick={onOpenFilters} aria-label="Open filters">
              <span style={S.filterInlineIcon}>☰</span>
              <span>Filters</span>
              {activeFilterCount > 0 && <b style={S.filterInlineBadge}>{activeFilterCount}</b>}
            </button>
            {activeFilterCount > 0 && (
              <button type="button" style={S.resetSmallBtn} onClick={onClear}>
                Clear All
              </button>
            )}
          </div>
        </div>
        <div style={S.activeFilterChips}>
          {chips.length === 0 ? (
            <span style={S.noFilters}>No filters applied.</span>
          ) : (
            chips.map((chip, index) => (
              <span key={`${chip.source}-${chip.key}-${chip.clientKey || chip.removeValue || chip.value || index}`} style={chip.notIn ? S.excludeChip : S.activeChip}>
                <span style={S.filterChipText}>
                  {chip.notIn ? "Non preferred " : ""}
                  {chip.label}: {chip.value}
                </span>
                <button
                  type="button"
                  style={S.filterChipRemove}
                  onClick={() => onRemoveChip?.(chip)}
                  aria-label={`Remove ${chip.label} filter`}
                >
                  x
                </button>
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function lookupById(items, id) {
  const found = safeArray(items).find((item) => Number(item.id) === Number(id));
  return found?.name || found?.display_label || String(id);
}



function FilterPanel({
  filters,
  nonPreferredFilters,
  nonVerifiedFilters,
  preferenceMode,
  setPreferenceMode,
  set,
  setGlobalPreferenceFilter,
  setNonVerifiedFilter,
  toggleArrayValue,
  toggleGlobalPreferenceArrayValue,
  toggleArrayTextValue,
  toggleGlobalPreferenceArrayTextValue,
  toggleEducationPursuingForId,
  toggleGlobalPreferencePursuingForId,
  addAddressFilter,
  addGlobalPreferenceAddressFilter,
  removeAddressFilter,
  removeGlobalPreferenceAddressFilter,
  setAddressFilterType,
  setGlobalPreferenceAddressFilterType,
  toggleAddressFilterArrayValue,
  toggleGlobalPreferenceAddressFilterArrayValue,
  lookups,
  filteredSakhas,
  filteredGlobalPreferenceSakhas,
  filteredDegreeNames,
  filteredGlobalPreferenceDegreeNames,
  hasDegreeCategoryMapping,
  search,
  clear,
  loading,
  activeFilterCount,
  resultsCount,
  onClose,
}) {
  const isNonPreferredMode = preferenceMode === "non_preferred";
  const isNonVerifiedMode = preferenceMode === "non_verified";
  const effectivePanelNonPreferredFilters = useMemo(
    () => sanitizeNonPreferredFilters(nonPreferredFilters, filters),
    [nonPreferredFilters, filters]
  );
  const activePreferenceFilters = isNonVerifiedMode
    ? nonVerifiedFilters
    : isNonPreferredMode
      ? effectivePanelNonPreferredFilters
      : filters;
  const activeSearchInputMode = normalizeSearchMode(activePreferenceFilters.search_by);
  const setPreferenceValue = isNonVerifiedMode ? setNonVerifiedFilter : isNonPreferredMode ? setGlobalPreferenceFilter : set;
  const togglePreferenceArrayValue = isNonPreferredMode ? toggleGlobalPreferenceArrayValue : toggleArrayValue;
  const togglePreferenceArrayTextValue = isNonPreferredMode ? toggleGlobalPreferenceArrayTextValue : toggleArrayTextValue;
  const togglePreferencePursuingForId = isNonPreferredMode ? toggleGlobalPreferencePursuingForId : toggleEducationPursuingForId;
  const addPreferenceAddressFilter = isNonPreferredMode ? addGlobalPreferenceAddressFilter : addAddressFilter;
  const removePreferenceAddressFilter = isNonPreferredMode ? removeGlobalPreferenceAddressFilter : removeAddressFilter;
  const setPreferenceAddressFilterType = isNonPreferredMode ? setGlobalPreferenceAddressFilterType : setAddressFilterType;
  const togglePreferenceAddressFilterArrayValue = isNonPreferredMode
    ? toggleGlobalPreferenceAddressFilterArrayValue
    : toggleAddressFilterArrayValue;
  const sakhaOptions = isNonPreferredMode ? filteredGlobalPreferenceSakhas : filteredSakhas;
  const degreeOptions = isNonPreferredMode ? filteredGlobalPreferenceDegreeNames : filteredDegreeNames;
  const visibleGenders = useMemo(
    () =>
      safeArray(lookups.genders).filter(
        (g) => String(g?.name || g?.display_label || "").trim().toLowerCase() !== "other"
      ),
    [lookups.genders]
  );
  const activeAddressFilters = safeArray(activePreferenceFilters.address_filters);
  const preferredAddressBlockedValues = useMemo(
    () => buildAddressBlockedValueSets(filters.address_filters),
    [filters.address_filters]
  );
  const preferredCasteIds = useMemo(
    () => new Set(safeArray(filters.caste_ids).map(Number).filter(Boolean)),
    [filters.caste_ids]
  );
  const preferredSakhaIds = useMemo(
    () => new Set(safeArray(filters.sakha_ids).map(Number).filter(Boolean)),
    [filters.sakha_ids]
  );
  const preferredSampradayIds = useMemo(
    () => new Set(safeArray(filters.sampraday_ids).map(Number).filter(Boolean)),
    [filters.sampraday_ids]
  );
  const preferredEducationIds = useMemo(
    () => new Set(safeArray(filters.education_category_ids).map(Number).filter(Boolean)),
    [filters.education_category_ids]
  );
  const preferredOccupationIds = useMemo(
    () => new Set(safeArray(filters.occupation_type_ids).map(Number).filter(Boolean)),
    [filters.occupation_type_ids]
  );
  const preferredDegreeNames = useMemo(
    () => new Set(safeArray(filters.degree_names).map((value) => String(value || "").trim().toLowerCase()).filter(Boolean)),
    [filters.degree_names]
  );
  const preferredMaritalStatusId = String(filters.marital_status_id || "").trim();
  const casteOptions = isNonPreferredMode
    ? lookups.castes.filter((c) => !preferredCasteIds.has(Number(c.id)))
    : lookups.castes;
  const visibleSakhaOptions = isNonPreferredMode
    ? sakhaOptions.filter((s) => !preferredSakhaIds.has(Number(s.id)))
    : sakhaOptions;
  const sampradayOptions = isNonPreferredMode
    ? lookups.sampradays.filter((sampraday) => !preferredSampradayIds.has(Number(sampraday.id)))
    : lookups.sampradays;
  const educationOptions = isNonPreferredMode
    ? lookups.educationCategories.filter((education) => !preferredEducationIds.has(Number(education.id)))
    : lookups.educationCategories;
  const visibleDegreeOptions = isNonPreferredMode
    ? safeArray(degreeOptions).filter((degree) => !preferredDegreeNames.has(String(degree.name || degree.display_label || "").trim().toLowerCase()))
    : safeArray(degreeOptions);
  const occupationOptions = isNonPreferredMode
    ? lookups.occupationTypes.filter((occupationType) => !preferredOccupationIds.has(Number(occupationType.id)))
    : lookups.occupationTypes;
  const maritalStatusOptions = isNonPreferredMode
    ? lookups.maritalStatus.filter((m) => String(m.id) !== preferredMaritalStatusId)
    : lookups.maritalStatus;
  const birthYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= MIN_BIRTH_YEAR; year -= 1) {
      years.push(String(year));
    }
    return years;
  }, []);
  const canApplyFilters = !loading && (!isNonVerifiedMode || hasSearchFilterValue(activePreferenceFilters));

  const setActivePreferenceSearchMode = (nextMode) => {
    const nextFilters = buildSearchFilterPatch(activePreferenceFilters, nextMode, "");
    setPreferenceValue("search_by", nextFilters.search_by);
    setPreferenceValue("q", nextFilters.q);
    setPreferenceValue("birth_date", nextFilters.birth_date);
  };

  const setActivePreferenceSearchValue = (nextValue) => {
    const nextFilters = buildSearchFilterPatch(activePreferenceFilters, activeSearchInputMode, nextValue);
    if (isDobSearchMode(activeSearchInputMode)) {
      setPreferenceValue("birth_date", nextFilters.birth_date);
      if (String(activePreferenceFilters.q || "")) setPreferenceValue("q", "");
      return;
    }
    setPreferenceValue("q", nextFilters.q);
    if (String(activePreferenceFilters.birth_date || "")) setPreferenceValue("birth_date", "");
  };

  const applyFiltersAndClose = (event) => {
    event?.preventDefault?.();
    if (!canApplyFilters) return;
    search();
    onClose?.();
  };

  return (
    <form style={S.filterPanelContent} onSubmit={applyFiltersAndClose}>
      <div style={S.filterPanelHeader}>
        <div>
          <p style={S.filterOverlayEyebrow}>Smart Filters</p>
          <h2 style={S.filterOverlayTitle}>Refine Matches</h2>
          <p style={S.filterOverlaySub}>
            Switch between preferred, non preferred, and non verified tabs, then apply the relevant search.
          </p>
        </div>

        {onClose && (
          <button type="button" style={S.filterCloseBtn} onClick={onClose}>
            ×
          </button>
        )}
      </div>

      <div style={S.filterOverlaySummary}>
        <span>{activeFilterCount} active filter{activeFilterCount !== 1 ? "s" : ""}</span>
        <b>{resultsCount} result{resultsCount !== 1 ? "s" : ""}</b>
      </div>

      <div style={S.preferenceTabs}>
        <button
          type="button"
          style={{ ...S.preferenceTab, ...(preferenceMode === "preferred" ? S.preferenceTabActive : {}) }}
          onClick={() => setPreferenceMode("preferred")}
        >
          Preferred
        </button>
        <button
          type="button"
          style={{ ...S.preferenceTab, ...(preferenceMode === "non_preferred" ? S.preferenceTabActive : {}) }}
          onClick={() => setPreferenceMode("non_preferred")}
        >
          Non Preferred
        </button>
        <button
          type="button"
          style={{ ...S.preferenceTab, ...(preferenceMode === "non_verified" ? S.preferenceTabActive : {}) }}
          onClick={() => setPreferenceMode("non_verified")}
        >
          Non Verified
        </button>
      </div>

      <div style={S.filterGrid} className="jk-filter-grid">
        {!isNonPreferredMode && (
          <NotInFilterGroup label="Quick Search">
            {isMobileSearchMode(activeSearchInputMode) ? (
              <IndianPhoneInput
                value={getSearchFilterValue(activePreferenceFilters)}
                onChange={setActivePreferenceSearchValue}
                wrapperStyle={S.filterPhoneInputWrap}
                selectWrapStyle={S.filterPhoneSelectWrap}
                selectStyle={S.filterPhoneSelect}
                inputStyle={S.input}
                placeholder={getSearchInputPlaceholder(activeSearchInputMode)}
              />
            ) : (
              <input
                type={getSearchInputType(activeSearchInputMode)}
                style={S.input}
                value={getSearchFilterValue(activePreferenceFilters)}
                onChange={(e) => setActivePreferenceSearchValue(e.target.value)}
                placeholder={getSearchInputPlaceholder(activeSearchInputMode)}
              />
            )}
            <div style={S.radioRow}>
              {SEARCH_MODE_OPTIONS.map((option) => (
                <label key={option.value} style={S.radioOption}>
                  <input
                    type="radio"
                    name={isNonVerifiedMode ? "search_by_non_verified" : "search_by"}
                    value={option.value}
                    checked={activeSearchInputMode === option.value}
                    onChange={(e) => setActivePreferenceSearchMode(e.target.value)}
                    style={S.radioInput}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {isNonVerifiedMode && (
              <div style={S.notInHint}>
                Search by profile number, name, mobile, or DOB to open unverified profiles for review and editing.
              </div>
            )}
          </NotInFilterGroup>
        )}

        {!isNonVerifiedMode && (
          <>
            {!isNonPreferredMode && (
              <>
                <NotInFilterGroup label="Gender">
                  <select style={S.select} value={activePreferenceFilters.gender_id} onChange={(e) => setPreferenceValue("gender_id", e.target.value)}>
                    <option value="">All genders</option>
                    {visibleGenders.map((g) => (
                      <option key={g.id} value={g.id}>{g.name || g.display_label}</option>
                    ))}
                  </select>
                </NotInFilterGroup>

                <NotInFilterGroup label="Birth Year Range">
                  <div style={S.ageRow}>
                    <select
                      style={S.ageInput}
                      value={activePreferenceFilters.birth_year_from}
                      onChange={(e) => setPreferenceValue("birth_year_from", e.target.value)}
                    >
                      <option value="">From year</option>
                      {birthYearOptions.map((year) => (
                        <option key={`from-${year}`} value={year}>{year}</option>
                      ))}
                    </select>
                    <span style={S.ageDash}>—</span>
                    <select
                      style={S.ageInput}
                      value={activePreferenceFilters.birth_year_to}
                      onChange={(e) => setPreferenceValue("birth_year_to", e.target.value)}
                    >
                      <option value="">To year</option>
                      {birthYearOptions.map((year) => (
                        <option key={`to-${year}`} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </NotInFilterGroup>

                <NotInFilterGroup label="Specific Date of Birth">
                  <input
                    style={S.input}
                    type="date"
                    value={activePreferenceFilters.birth_date}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setPreferenceValue("birth_date", e.target.value)}
                  />
                </NotInFilterGroup>

                <div style={S.filterGroup}>
                  <div style={S.filterLabelRow}>
                    <label style={S.filterLabel}>Height Range</label>
                  </div>
                  <div style={S.heightRangeGrid} className="jk-height-range-grid">
                    <HeightRangeBoundInput
                      label="From"
                      valueCm={activePreferenceFilters.height_cm_from}
                      onChangeCm={(value) => setPreferenceValue("height_cm_from", value)}
                    />
                    <HeightRangeBoundInput
                      label="To"
                      valueCm={activePreferenceFilters.height_cm_to}
                      onChangeCm={(value) => setPreferenceValue("height_cm_to", value)}
                    />
                  </div>
                </div>
              </>
            )}

        <div style={{ ...S.filterGroup, gridColumn: "1 / -1" }}>
          <div style={S.addressSectionHead}>
            <div>
              <label style={S.filterLabel}>Address Type Filters</label>
              <div style={S.filterHint}>Use these combined address filters instead of separate location filters.</div>
            </div>
            <button type="button" style={S.addAddressFilterBtn} onClick={addPreferenceAddressFilter}>
              + Add Address Filter
            </button>
          </div>

          {activeAddressFilters.length === 0 ? (
            <div style={S.addressEmptyState}>Add current, native, family, or office location filters here.</div>
          ) : (
            <div style={S.addressFilterStack}>
              {activeAddressFilters.map((addressFilter) => (
                <AddressFilterCard
                  key={addressFilter.client_key}
                  filter={addressFilter}
                  locations={lookups.locations}
                  blockedValues={isNonPreferredMode ? preferredAddressBlockedValues : null}
                  onTypeChange={setPreferenceAddressFilterType}
                  onToggleValue={togglePreferenceAddressFilterArrayValue}
                  onRemove={removePreferenceAddressFilter}
                />
              ))}
            </div>
          )}
        </div>

        <NotInFilterGroup label="Marital Status">
          <select style={S.select} value={activePreferenceFilters.marital_status_id} onChange={(e) => setPreferenceValue("marital_status_id", e.target.value)}>
            <option value="">Any status</option>
            {maritalStatusOptions.map((m) => (
              <option key={m.id} value={m.id}>{m.name || m.display_label}</option>
            ))}
          </select>
        </NotInFilterGroup>

        <NotInFilterGroup label="Caste">
          <MultiSelectDropdown
            placeholder="Select caste"
            options={casteOptions.map((c) => ({ id: c.id, label: c.name }))}
            selected={activePreferenceFilters.caste_ids}
            onToggle={(id) => togglePreferenceArrayValue("caste_ids", id)}
          />
        </NotInFilterGroup>

        <NotInFilterGroup label="Sakha">
          <MultiSelectDropdown
            placeholder="Select sakha"
            options={visibleSakhaOptions.map((s) => ({ id: s.id, label: s.name }))}
            selected={activePreferenceFilters.sakha_ids}
            onToggle={(id) => togglePreferenceArrayValue("sakha_ids", id)}
          />
        </NotInFilterGroup>

        <NotInFilterGroup label="Sampraday">
          <MultiSelectDropdown
            placeholder="Select sampraday"
            options={sampradayOptions.map((sampraday) => ({ id: sampraday.id, label: sampraday.name }))}
            selected={activePreferenceFilters.sampraday_ids}
            onToggle={(id) => togglePreferenceArrayValue("sampraday_ids", id)}
          />
        </NotInFilterGroup>

        <NotInFilterGroup label="Education">
          <EducationMultiSelectDropdown
            placeholder="Select education"
            options={educationOptions.map((ec) => ({ id: ec.id, label: ec.name }))}
            selected={activePreferenceFilters.education_category_ids}
            onToggle={(id) => togglePreferenceArrayValue("education_category_ids", id)}
            statusById={activePreferenceFilters.education_status_by_id}
            onTogglePursuing={togglePreferencePursuingForId}
          />
        </NotInFilterGroup>

        <div style={S.filterGroup}>
          <div style={S.filterLabelRow}>
            <label style={S.filterLabel}>Degree Name</label>
          </div>
          <MultiSelectDropdown
            placeholder={
              hasDegreeCategoryMapping && !safeArray(activePreferenceFilters.education_category_ids).length
                ? "Select education category first"
                : "Select degree"
            }
            options={visibleDegreeOptions.map((degree) => ({
              id: String(degree.name || degree.display_label || "").trim(),
              label: String(degree.name || degree.display_label || "").trim(),
            }))}
            selected={activePreferenceFilters.degree_names}
            onToggle={(value) => togglePreferenceArrayTextValue("degree_names", value)}
            disabled={hasDegreeCategoryMapping && !safeArray(activePreferenceFilters.education_category_ids).length}
          />
        </div>

        <NotInFilterGroup label="Occupation">
          <MultiSelectDropdown
            placeholder="Select occupation"
            options={occupationOptions.map((occupationType) => ({ id: occupationType.id, label: occupationType.name || occupationType.display_label }))}
            selected={activePreferenceFilters.occupation_type_ids}
            onToggle={(id) => togglePreferenceArrayValue("occupation_type_ids", id)}
          />
        </NotInFilterGroup>

        {!isNonPreferredMode && (
          <NotInFilterGroup label="Tag">
          <MultiSelectDropdown
            placeholder="Select tags"
            options={lookups.tags.map((t) => ({ id: t.id, label: t.name }))}
            selected={activePreferenceFilters.tag_ids}
            onToggle={(id) => togglePreferenceArrayValue("tag_ids", id)}
          />
          </NotInFilterGroup>
        )}
          </>
        )}
      </div>

      <div style={S.filterOverlayFooter}>
        <button type="button" style={S.clearBtn} onClick={clear}>
          Clear filters
        </button>
        <button
          type="submit"
          style={S.applyFilterBtn}
          disabled={!canApplyFilters}
        >
          {loading
            ? "Searching..."
            : isNonVerifiedMode
              ? "Search Unverified"
              : `Apply Filters${activeFilterCount ? ` (${activeFilterCount})` : ""}`}
        </button>
      </div>
    </form>
  );
}

function AddressFilterCard({ filter, locations, blockedValues, onTypeChange, onToggleValue, onRemove }) {
  const options = useMemo(() => buildAddressFilterOptions(filter, locations, blockedValues), [filter, locations, blockedValues]);
  const hasType = !!filter.address_type;
  const [open, setOpen] = useState(true);

  return (
    <div style={S.addressFilterCard}>
      <div style={S.addressFilterCardTop}>
        <button type="button" style={S.addressCollapseBtn} onClick={() => setOpen((value) => !value)}>
          <div>
            <p style={S.addressFilterCardTitle}>{labelizeAddressType(filter.address_type || "address")}</p>
          </div>
          <span style={S.addressCollapseIcon}>{open ? "−" : "+"}</span>
        </button>
        <button type="button" style={S.addressRemoveBtn} onClick={() => onRemove(filter.client_key)}>
          Remove
        </button>
      </div>

      {open && (
      <div style={S.addressFilterGrid} className="jk-address-filter-grid">
        <div style={S.filterGroup}>
          <div style={S.filterLabelRow}>
            <label style={S.filterLabel}>Address Type</label>
          </div>
          <select
            style={S.select}
            value={filter.address_type || ""}
            onChange={(event) => onTypeChange(filter.client_key, event.target.value)}
          >
            <option value="">Select address type</option>
            {ADDRESS_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div style={S.filterGroup}>
          <div style={S.filterLabelRow}>
            <label style={S.filterLabel}>Countries</label>
          </div>
          <MultiSelectDropdown
            placeholder={hasType ? "Select countries" : "Select address type first"}
            options={options.countries}
            selected={filter.country_ids}
            onToggle={(id) => onToggleValue(filter.client_key, "country_ids", id)}
            disabled={!hasType}
          />
        </div>

        <div style={S.filterGroup}>
          <div style={S.filterLabelRow}>
            <label style={S.filterLabel}>States</label>
          </div>
          <MultiSelectDropdown
            placeholder={hasType ? "Select states" : "Select address type first"}
            options={options.states}
            selected={filter.state_ids}
            onToggle={(id) => onToggleValue(filter.client_key, "state_ids", id)}
            disabled={!hasType}
          />
        </div>

        <div style={S.filterGroup}>
          <div style={S.filterLabelRow}>
            <label style={S.filterLabel}>Districts</label>
          </div>
          <MultiSelectDropdown
            placeholder={hasType ? "Select districts" : "Select address type first"}
            options={options.districts}
            selected={filter.district_ids}
            onToggle={(id) => onToggleValue(filter.client_key, "district_ids", id)}
            disabled={!hasType}
          />
        </div>
      </div>
      )}
    </div>
  );
}


function ProfileCard({
  profile: p,
  locationMaps,
  mode,
  onView,
  onToggleSelection = null,
  selectionMode = false,
  selectionCardMode = "buttons",
  canViewPersonaLabel = false,
  canViewCompleteness = false,
  canViewProfile = true,
  actionLabel = "View Profile",
  actionActiveLabel = "Selected",
  actionActive = false,
  showSecondaryAction = false,
  secondaryActionLabel = "View",
  onSecondaryAction = null,
}) {
  const [hovered, setHovered] = useState(false);
  const [imgError, setImgError] = useState(false);

  const age = getAge(p.birth_date || p.dob);
  const fullName = getFullName(p);
  const initials = `${p.first_name?.[0] || ""}${p.last_name?.[0] || ""}`.toUpperCase();
  const avatarBg = pickColor(p.first_name || "A");
  const preferredPhoto = String(p.primary_photo_url_cache || "").trim();
  const fallbackPhoto = String(p.photo || "").trim();
  const alternatePhoto = fallbackPhoto && fallbackPhoto !== preferredPhoto ? fallbackPhoto : "";
  const [photoSrc, setPhotoSrc] = useState(preferredPhoto || fallbackPhoto || "");
  const showImage = photoSrc && !imgError;

  useEffect(() => {
    setImgError(false);
    setPhotoSrc(preferredPhoto || fallbackPhoto || "");
  }, [preferredPhoto, fallbackPhoto, p.id]);

  const handlePhotoError = () => {
    if (alternatePhoto && photoSrc !== alternatePhoto) {
      setPhotoSrc(alternatePhoto);
      return;
    }
    setImgError(true);
  };

  const geo = getProfileGeo(p, locationMaps);
  const location =
    p.location_display_cache ||
    [geo.city, geo.district, geo.state, geo.country].filter(Boolean).join(", " );

  const education = p.education_summary || p.education_category || "";
  const educationStatus = normalizeEducationStatusValue(p.education_status_cache);
  const occupation = p.current_occupation_details || p.occupation_type || p.work_profile || "";
  const rawCompletenessPct = p.completeness?.completeness_pct ?? p.completeness_pct_cache;
  const hasCompletenessPct =
    rawCompletenessPct !== null &&
    rawCompletenessPct !== undefined &&
    String(rawCompletenessPct).trim() !== "";
  const completenessPct = hasCompletenessPct
    ? Math.max(0, Math.min(Number(rawCompletenessPct) || 0, 100))
    : null;
  const personaLabel = canViewPersonaLabel ? labelizePersonaTier(p.persona_tier_cache) : "";
  const checkboxSelectionMode = selectionMode && selectionCardMode === "checkbox";
  const metaItems = [
    age ? { key: "age", icon: <CalendarIcon color={C.accent} />, value: `${age} yrs` } : null,
    p.gender ? { key: "gender", icon: <UserIcon color={C.accent} />, value: p.gender } : null,
    p.marital_status ? { key: "status", icon: <StatusIcon color={C.accent} />, value: p.marital_status } : null,
  ].filter(Boolean);

  const handleCardSelect = () => {
    if (checkboxSelectionMode && typeof onToggleSelection === "function") {
      onToggleSelection();
    }
  };

  if (mode === "list") {
    return (
      <div
        style={{
          ...S.listCard,
          ...(actionActive ? S.listCardSelected : {}),
          ...(hovered ? S.listCardHover : {}),
          ...(checkboxSelectionMode ? S.selectableCard : {}),
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={checkboxSelectionMode ? handleCardSelect : undefined}
      >
        <div style={S.listPhotoWrap}>
          {showImage ? (
            <>
              <div style={S.mediaBackdrop}>
                <img src={photoSrc} alt="" style={S.mediaBackdropImg} aria-hidden="true" />
              </div>
              <div style={S.listMediaInner}>
                <img src={photoSrc} alt={fullName} style={S.listImg} onError={handlePhotoError} />
              </div>
            </>
          ) : (
            <div style={{ ...S.listAvatar, background: avatarBg }}>
              <span style={S.listInitials}>{initials || "?"}</span>
            </div>
          )}
        </div>

        <div style={S.listBody}>
          <div style={S.listTop}>
            <div style={S.listTopMain}>
              <h3 style={S.listName}>{fullName}</h3>
              <p style={S.listMeta}>Profile #{getProfileNumberDisplay(p, "-")}</p>
            </div>
            <div style={S.listTopAside}>
              {personaLabel && <span style={S.tierBadge}>{personaLabel}</span>}
              {checkboxSelectionMode && (
                <input
                  type="checkbox"
                  checked={actionActive}
                  onChange={() => onToggleSelection?.()}
                  onClick={(event) => event.stopPropagation()}
                  style={S.selectionCheckbox}
                  aria-label={`Select ${fullName}`}
                />
              )}
            </div>
          </div>

          {metaItems.length > 0 && (
            <div style={S.metaRow}>
              {metaItems.map((item) => (
                <span key={item.key} style={S.metaItem}>
                  <span style={S.metaIcon}>{item.icon}</span>
                  <span>{item.value}</span>
                </span>
              ))}
            </div>
          )}

          {location && (
            <div style={S.locationMeta}>
              <span style={S.metaIcon}><LocationIcon color={C.accent} /></span>
              <span>{location}</span>
            </div>
          )}

          <div style={S.pillRow}>
            {education && <Pill>{education}</Pill>}
            {educationStatus === "pursuing" && <Pill>Pursuing</Pill>}
            {occupation && <Pill>{occupation}</Pill>}
          </div>

          {p.about_me && <p style={S.cardAbout}>{truncate(p.about_me, 150)}</p>}
        </div>

        {canViewProfile && (
          <div
            style={{
              ...S.cardActionRow,
              ...(showSecondaryAction && typeof onSecondaryAction === "function" ? S.cardActionRowSplit : {}),
            }}
          >
            <button
              type="button"
              style={{ ...S.viewBtn, ...(!checkboxSelectionMode && actionActive ? S.viewBtnActive : {}) }}
              onClick={(event) => {
                event.stopPropagation();
                onView();
              }}
            >
              {actionActive ? actionActiveLabel : actionLabel}
            </button>
            {showSecondaryAction && typeof onSecondaryAction === "function" && (
              <button
                type="button"
                style={S.secondaryActionBtn}
                onClick={(event) => {
                  event.stopPropagation();
                  onSecondaryAction();
                }}
              >
                {secondaryActionLabel}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        ...S.card,
        ...(actionActive ? S.cardSelected : {}),
        ...(hovered ? S.cardHover : {}),
        ...(checkboxSelectionMode ? S.selectableCard : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={checkboxSelectionMode ? handleCardSelect : undefined}
    >
      <div style={S.cardMediaWrap}>
        {showImage ? (
          <>
            <div style={S.mediaBackdrop}>
              <img src={photoSrc} alt="" style={S.mediaBackdropImg} aria-hidden="true" />
            </div>
            <div style={S.cardMediaInner}>
              <img src={photoSrc} alt={fullName} style={S.cardImg} onError={handlePhotoError} />
            </div>
          </>
        ) : (
          <div style={{ ...S.cardAvatar, background: avatarBg }}>
            <span style={S.cardInitials}>{initials || "?"}</span>
          </div>
        )}

        <div style={S.photoOverlay}>
          {getProfileNumberDisplay(p, "") && <span style={S.profileNo}>#{getProfileNumberDisplay(p)}</span>}
          {personaLabel && <span style={S.tierOverlay}>{personaLabel}</span>}
        </div>
      </div>

      <div style={S.cardBody}>
        <div style={S.cardTitleRow}>
          <h3 style={S.cardName}>{fullName}</h3>
          {checkboxSelectionMode && (
            <input
              type="checkbox"
              checked={actionActive}
              onChange={() => onToggleSelection?.()}
              onClick={(event) => event.stopPropagation()}
              style={S.selectionCheckbox}
              aria-label={`Select ${fullName}`}
            />
          )}
        </div>

        {(metaItems.length > 0 || educationStatus === "pursuing") && (
          <div style={S.metaRow}>
            {metaItems.map((item) => (
              <span key={item.key} style={S.metaItem}>
                <span style={S.metaIcon}>{item.icon}</span>
                <span>{item.value}</span>
              </span>
            ))}
            {educationStatus === "pursuing" && (
              <span style={S.metaItem}>
                <span style={S.metaIcon}><StatusIcon color={C.accent} /></span>
                <span>Pursuing</span>
              </span>
            )}
          </div>
        )}

        {location && (
          <div style={S.locationMeta}>
            <span style={S.metaIcon}><LocationIcon color={C.accent} /></span>
            <span>{location}</span>
          </div>
        )}

        {(education || occupation || (canViewCompleteness && completenessPct !== null)) && (
          <div style={S.cardDetailList}>
            {education && (
              <div style={S.cardDetailItem}>
                <span style={S.metaIcon}>
                  <EducationIcon color={C.accent} />
                </span>
                <span style={{ ...S.cardDetailText, ...S.cardDetailTextSingleLine }}>{education}</span>
              </div>
            )}
            {occupation && (
              <div style={S.cardDetailItem}>
                <span style={S.metaIcon}>
                  <WorkIcon color={C.accent} />
                </span>
                <span style={S.cardDetailText}>{occupation}</span>
              </div>
            )}
            {canViewCompleteness && completenessPct !== null && (
              <div style={S.cardDetailItem}>
                <span style={S.metaIcon}>
                  <CompletenessIcon pct={completenessPct} color={C.accent} />
                </span>
                <span style={S.cardDetailText}>{completenessPct}% complete</span>
              </div>
            )}
          </div>
        )}

        {p.about_me && <p style={S.cardAbout}>{truncate(p.about_me, 88)}</p>}

        {canViewProfile && (
          <div
            style={{
              ...S.cardActionRow,
              ...(showSecondaryAction && typeof onSecondaryAction === "function" ? S.cardActionRowSplit : {}),
            }}
          >
            <button
              type="button"
              style={{
                ...S.viewBtn,
                ...(!checkboxSelectionMode && actionActive ? S.viewBtnActive : {}),
                ...(!checkboxSelectionMode && !actionActive && hovered ? S.viewBtnHover : {}),
              }}
              onClick={(event) => {
                event.stopPropagation();
                onView();
              }}
            >
              {actionActive ? actionActiveLabel : actionLabel}
            </button>
            {showSecondaryAction && typeof onSecondaryAction === "function" && (
              <button
                type="button"
                style={S.secondaryActionBtn}
                onClick={(event) => {
                  event.stopPropagation();
                  onSecondaryAction();
                }}
              >
                {secondaryActionLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
function NotInFilterGroup({ label, children }) {
  return (
    <div style={S.filterGroup}>
      <div style={S.filterLabelRow}>
        <label style={S.filterLabel}>{label}</label>
      </div>
      {children}
    </div>
  );
}

function MultiSelectDropdown({ placeholder, options, selected, onToggle, tone = "primary", disabled = false }) {
  const [query, setQuery] = useState("");
  const selectedSet = useMemo(
    () => new Set(safeArray(selected).map(normalizeSelectValue)),
    [selected]
  );
  const selectedOptions = useMemo(
    () => safeArray(options).filter((option) => selectedSet.has(normalizeSelectValue(option.id))),
    [options, selectedSet]
  );
  const visibleOptions = useMemo(() => {
    const filteredOptions = safeArray(options).filter((option) =>
      String(option.label || "").toLowerCase().includes(query.trim().toLowerCase())
    );
    return orderSelectedOptionsFirst(filteredOptions, (option) => selectedSet.has(normalizeSelectValue(option.id)));
  }, [options, query, selectedSet]);
  const summary =
    selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length <= 2
        ? selectedOptions.map((option) => option.label).join(", ")
        : `${selectedOptions.slice(0, 2).map((option) => option.label).join(", ")} +${selectedOptions.length - 2}`;

  return (
    <details style={{ ...S.multiDropdown, ...(disabled ? S.multiDropdownDisabled : {}) }}>
      <summary style={{ ...S.multiDropdownSummary, ...(disabled ? S.multiDropdownSummaryDisabled : {}) }}>
        <span>{summary}</span>
        <b style={S.multiDropdownCount}>{selectedOptions.length}</b>
      </summary>

      <div style={S.multiDropdownMenu}>
        {!disabled && (
          <input
            type="text"
            style={S.multiDropdownSearch}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search options..."
          />
        )}

        {disabled ? (
          <div style={S.multiDropdownEmpty}>{placeholder}</div>
        ) : (
          <div style={S.multiDropdownOptions}>
            {visibleOptions.length === 0 ? (
              <div style={S.multiDropdownEmpty}>No options available.</div>
            ) : (
              visibleOptions.map((option) => {
                const active = selectedSet.has(normalizeSelectValue(option.id));
                const activeStyle = tone === "alt" ? S.checkboxOptionActiveAlt : S.checkboxOptionActive;

                return (
                  <label
                    key={option.id}
                    style={{ ...S.checkboxOption, ...(active ? activeStyle : {}) }}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => onToggle(option.id)}
                      style={S.checkboxInput}
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })
            )}
          </div>
        )}
      </div>
    </details>
  );
}

function EducationMultiSelectDropdown({
  placeholder,
  options,
  selected,
  onToggle,
  statusById,
  onTogglePursuing,
  disabled = false,
}) {
  const [query, setQuery] = useState("");
  const selectedSet = useMemo(
    () => new Set(safeArray(selected).map(Number)),
    [selected]
  );
  const selectedOptions = useMemo(
    () => safeArray(options).filter((option) => selectedSet.has(Number(option.id))),
    [options, selectedSet]
  );
  const visibleOptions = useMemo(() => {
    const filteredOptions = safeArray(options).filter((option) =>
      String(option.label || "").toLowerCase().includes(query.trim().toLowerCase())
    );
    return orderSelectedOptionsFirst(filteredOptions, (option) => selectedSet.has(Number(option.id)));
  }, [options, query, selectedSet]);
  const summary =
    selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length <= 2
        ? selectedOptions.map((option) => option.label).join(", ")
        : `${selectedOptions.slice(0, 2).map((option) => option.label).join(", ")} +${selectedOptions.length - 2}`;

  return (
    <details style={{ ...S.multiDropdown, ...(disabled ? S.multiDropdownDisabled : {}) }}>
      <summary style={{ ...S.multiDropdownSummary, ...(disabled ? S.multiDropdownSummaryDisabled : {}) }}>
        <span>{summary}</span>
        <b style={S.multiDropdownCount}>{selectedOptions.length}</b>
      </summary>

      <div style={S.multiDropdownMenu}>
        {!disabled && (
          <input
            type="text"
            style={S.multiDropdownSearch}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search options..."
          />
        )}

        {disabled ? (
          <div style={S.multiDropdownEmpty}>{placeholder}</div>
        ) : (
          <div style={S.multiDropdownOptions}>
            {visibleOptions.length === 0 ? (
              <div style={S.multiDropdownEmpty}>No options available.</div>
            ) : (
              visibleOptions.map((option) => {
                const optionId = Number(option.id);
                const active = selectedSet.has(optionId);
                const isPursuing = normalizeEducationStatusValue(statusById?.[optionId]) === "pursuing";

                return (
                  <div
                    key={option.id}
                    style={{ ...S.checkboxOption, ...(active ? S.checkboxOptionActive : {}), ...(active ? S.educationOptionActive : {}) }}
                  >
                    <label style={S.educationOptionLabel}>
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => onToggle(option.id)}
                        style={S.checkboxInput}
                      />
                      <span>{option.label}</span>
                    </label>

                    {active && (
                      <button
                        type="button"
                        style={{ ...S.educationStatusBtn, ...(isPursuing ? S.educationStatusBtnActive : {}) }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onTogglePursuing(optionId);
                        }}
                      >
                        Pursuing
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </details>
  );
}

function HeightRangeBoundInput({ label, valueCm, onChangeCm }) {
  return (
    <div style={S.heightRangeField}>
      <label style={S.heightRangeLabel}>{label}</label>
      <select
        style={S.heightRangeSelect}
        value={normalizeHeightDropdownValue(valueCm)}
        onChange={(event) => onChangeCm(event.target.value)}
      >
        <option value="">Any height</option>
        {HEIGHT_RANGE_OPTIONS.map((option) => (
          <option key={`${label}-${option.value}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Pill({ children }) {
  return <span style={S.pill}>{children}</span>;
}

function ResultsIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3" stroke={color} strokeWidth="1.8" />
      <path d="M4.5 17c.8-2.3 2.7-3.5 4.5-3.5s3.7 1.2 4.5 3.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="17" cy="9" r="2.5" stroke={color} strokeWidth="1.8" />
      <path d="M15 16.5c.5-1.6 1.8-2.5 3.1-2.5 1 0 2 .5 2.8 1.6" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function MaleIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="15" r="5" stroke={color} strokeWidth="1.8" />
      <path d="M13 11l6-6" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14.5 5H19v4.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FemaleIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="4.5" stroke={color} strokeWidth="1.8" />
      <path d="M12 12.5v8" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8.5 17h7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function AlertSummaryIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3.5a4.5 4.5 0 0 0-4.5 4.5v2.1c0 .77-.22 1.53-.63 2.18L5.7 14.1c-.44.7.06 1.6.89 1.6h10.82c.83 0 1.33-.9.89-1.6l-1.17-1.82a4.13 4.13 0 0 1-.63-2.18V8A4.5 4.5 0 0 0 12 3.5Z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M10.2 18a1.9 1.9 0 0 0 3.6 0" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M12 8.1v2.85" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="12.95" r=".8" fill={color} />
    </svg>
  );
}

function AssignedProfilesIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="4.5" width="11" height="15" rx="2.5" stroke={color} strokeWidth="1.8" />
      <path d="M8 9h3.8M8 12.5h3.8M8 16h2.4" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="18" cy="9" r="2.7" stroke={color} strokeWidth="1.8" />
      <path d="M14.8 18c.7-1.8 2.1-2.8 3.8-2.8 1.7 0 3.1 1 3.8 2.8" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function AssignedTasksIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4.5" y="5" width="15" height="14" rx="2.6" stroke={color} strokeWidth="1.8" />
      <path d="M8 9.2h7.5M8 12.5h7.5M8 15.8h4.2" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M6.8 9.3l.8.8 1.4-1.6M6.8 15.8l.8.8 1.4-1.6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CalendarIcon({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="6" width="16" height="14" rx="2.5" stroke={color} strokeWidth="1.8" />
      <path d="M8 4v4M16 4v4M4 10h16" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function UserIcon({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.2" stroke={color} strokeWidth="1.8" />
      <path d="M6.5 18c1-2.4 3.1-3.7 5.5-3.7 2.4 0 4.5 1.3 5.5 3.7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function StatusIcon({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="7" stroke={color} strokeWidth="1.8" />
    </svg>
  );
}

function LocationIcon({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20s5.5-5.2 5.5-9.4A5.5 5.5 0 1 0 6.5 10.6C6.5 14.8 12 20 12 20Z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="10.5" r="1.8" fill={color} />
    </svg>
  );
}

function EducationIcon({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3.5 8.8 12 4.8l8.5 4-8.5 4-8.5-4Z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M7.5 10.8v3.4c0 1.4 2 2.8 4.5 2.8s4.5-1.4 4.5-2.8v-3.4" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20.5 9.2v5.1" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function WorkIcon({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="7.5" width="16" height="11" rx="2.5" stroke={color} strokeWidth="1.8" />
      <path d="M9 7.5V6.7A1.7 1.7 0 0 1 10.7 5h2.6A1.7 1.7 0 0 1 15 6.7v.8" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M4 12h16" stroke={color} strokeWidth="1.8" />
    </svg>
  );
}

function CompletenessIcon({ pct = 0, size = 14, color = "currentColor" }) {
  const normalizedPct = Math.max(0, Math.min(Number(pct) || 0, 100));
  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = circumference * (1 - normalizedPct / 100);

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r={radius} stroke={color} strokeOpacity="0.24" strokeWidth="2.2" />
      <circle
        cx="12"
        cy="12"
        r={radius}
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeOffset}
        transform="rotate(-90 12 12)"
      />
    </svg>
  );
}

function StatCard({ label, value, icon, hint = "", onClick }) {
  const clickable = typeof onClick === "function";
  const Component = clickable ? "button" : "div";

  return (
    <Component
      type={clickable ? "button" : undefined}
      style={{
        ...S.statCard,
        ...(clickable ? S.statCardClickable : S.statCardStatic),
      }}
      onClick={clickable ? onClick : undefined}
    >
      <span style={S.statBadge}>{icon}</span>
      <div style={S.statContent}>
        <b style={S.statValue}>{value}</b>
        <span style={S.statLabel}>{label}</span>
        {hint ? <span style={S.statHint}>{hint}</span> : null}
      </div>
    </Component>
  );
}

function Splash({ title, subtitle, action }) {
  return (
    <div style={S.splash}>
      <div style={S.splashOrb} />
      <p style={S.splashTitle}>{title}</p>
      <p style={S.splashSub}>{subtitle}</p>
      {action}
    </div>
  );
}

function buildOptionMaps(lookups) {
  const byId = (items, labelGetter = (item) => item.name || item.display_label) => {
    const map = new Map();
    safeArray(items).forEach((item) => {
      if (item.id != null) map.set(Number(item.id), labelGetter(item));
    });
    return map;
  };

  return {
    genders: byId(lookups.genders, (item) => item.name || item.display_label),
    maritalStatus: byId(lookups.maritalStatus, (item) => item.name || item.display_label),
    occupationTypes: byId(lookups.occupationTypes, (item) => item.name || item.display_label),
    castes: byId(lookups.castes, (item) => item.name),
    sakhas: byId(lookups.sakhas, (item) => item.name),
    sampradays: byId(lookups.sampradays, (item) => item.name),
    educationCategories: byId(lookups.educationCategories, (item) => item.name),
    tags: byId(lookups.tags, (item) => item.name),
  };
}

function filterLocationsBySearchPolicy(locations, searchPolicy) {
  const rows = safeArray(locations);
  const allowedCityIds = new Set(safeArray(searchPolicy?.allowed_city_ids).map(Number).filter(Boolean));
  const allowedStateIds = new Set(safeArray(searchPolicy?.allowed_state_ids).map(Number).filter(Boolean));

  return rows.filter((location) => {
    const cityId = Number(location.id);
    const stateId = Number(location.state_id || location.stateId || location.stateID);

    if (allowedCityIds.size && !allowedCityIds.has(cityId)) return false;
    if (allowedStateIds.size && !allowedStateIds.has(stateId)) return false;
    return true;
  });
}

function textIncludes(haystack, needle) {
  if (!needle) return true;
  return String(haystack || "").toLowerCase().includes(String(needle || "").toLowerCase());
}

function sameText(a, b) {
  if (!a || !b) return false;
  return String(a).toLowerCase() === String(b).toLowerCase();
}

function getLookupLabel(map, id) {
  if (!id) return "";
  return map.get(Number(id)) || "";
}


function getCountryName(location) {
  return String(location?.country || location?.country_name || location?.countryName || "India").trim();
}

function buildLocationMaps(locations) {
  const byCityId = new Map();
  const statesById = new Map();
  const districtsById = new Map();
  const tehsilsById = new Map();

  safeArray(locations).forEach((loc) => {
    if (loc.id != null) byCityId.set(Number(loc.id), loc);

    if (loc.state_id != null && !statesById.has(Number(loc.state_id))) {
      statesById.set(Number(loc.state_id), {
        id: loc.state_id,
        name: loc.state || loc.state_name,
        country: getCountryName(loc),
        country_id: loc.country_id || loc.countryId || loc.countryID,
      });
    }

    if (loc.district_id != null && !districtsById.has(Number(loc.district_id))) {
      districtsById.set(Number(loc.district_id), {
        id: loc.district_id,
        name: loc.district || loc.district_name,
        state_id: loc.state_id,
      });
    }

    if (loc.tehsil_id != null && !tehsilsById.has(Number(loc.tehsil_id))) {
      tehsilsById.set(Number(loc.tehsil_id), {
        id: loc.tehsil_id,
        name: loc.tehsil || loc.tehsil_name,
        district_id: loc.district_id,
      });
    }
  });

  return { byCityId, statesById, districtsById, tehsilsById };
}

function getProfileGeo(profile, locationMaps) {
  const cityCtx = profile.city_id ? locationMaps.byCityId.get(Number(profile.city_id)) : null;

  return {
    city_id: profile.city_id ? Number(profile.city_id) : cityCtx?.id ? Number(cityCtx.id) : null,
    city: profile.city || cityCtx?.city || cityCtx?.name || "",
    tehsil_id: profile.tehsil_id ? Number(profile.tehsil_id) : cityCtx?.tehsil_id ? Number(cityCtx.tehsil_id) : null,
    tehsil: profile.tehsil || cityCtx?.tehsil || cityCtx?.tehsil_name || "",
    district_id: profile.district_id ? Number(profile.district_id) : cityCtx?.district_id ? Number(cityCtx.district_id) : null,
    district: profile.district || cityCtx?.district || cityCtx?.district_name || "",
    state_id: profile.state_id ? Number(profile.state_id) : cityCtx?.state_id ? Number(cityCtx.state_id) : null,
    state: profile.state || cityCtx?.state || cityCtx?.state_name || "",
    country: profile.country || cityCtx?.country || "India",
    country_id: profile.country_id || cityCtx?.country_id || null,
  };
}

function applyLocalNotInFilters(rows, filters, locationMaps, optionMaps) {
  return safeArray(rows).filter((profile) => {
    const fullName = getFullName(profile);

    if (filters.q) {
      const q = String(filters.q);
      const matches = isProfileNoDisplaySearchMode(filters.search_by)
        ? textIncludes(getProfileNumberDisplay(profile), q)
        : (
            textIncludes(fullName, q) ||
            textIncludes(profile.normalized_name, q) ||
            textIncludes(profile.full_name_search, q)
          );

      if (matches) return false;
    }

    if (filters.gender_id) {
      const selected = getLookupLabel(optionMaps.genders, filters.gender_id);
      const same = sameText(profile.gender, selected);
      if (same) return false;
    }

    if (filters.birth_date) {
      const profileDate = String(profile.birth_date || profile.dob || "").slice(0, 10);
      if (profileDate === String(filters.birth_date).slice(0, 10)) return false;
    }

    if (filters.birth_year_from || filters.birth_year_to) {
      const birthYear = profile.birth_date || profile.dob ? new Date(profile.birth_date || profile.dob).getFullYear() : null;
      const from = filters.birth_year_from ? Number(filters.birth_year_from) : null;
      const to = filters.birth_year_to ? Number(filters.birth_year_to) : null;
      const inRange = birthYear != null && !Number.isNaN(birthYear) && (from == null || birthYear >= from) && (to == null || birthYear <= to);

      if (inRange) return false;
    }

    if (filters.height_cm_from || filters.height_cm_to) {
      const normalizedHeightRange = getNormalizedHeightRange(filters);
      const heightCm = Number(profile.height_cm);
      const from = normalizedHeightRange.from ? Number(normalizedHeightRange.from) : null;
      const to = normalizedHeightRange.to ? Number(normalizedHeightRange.to) : null;
      const inRange = Number.isFinite(heightCm) && (from == null || heightCm >= from) && (to == null || heightCm <= to);

      if (inRange) return false;
    }

    const addressFilters = getActiveAddressFilters(filters.address_filters);
    if (addressFilters.length && addressFilters.some((filter) => profileMatchesAddressFilter(profile, filter, locationMaps))) {
      return false;
    }

    if (filters.marital_status_id) {
      const selected = getLookupLabel(optionMaps.maritalStatus, filters.marital_status_id);
      const same = sameText(profile.marital_status, selected);
      if (same) return false;
    }

    if (safeArray(filters.caste_ids).length) {
      const same = safeArray(filters.caste_ids).map(Number).includes(Number(profile.caste_id));
      if (same) return false;
    }

    if (safeArray(filters.sakha_ids).length) {
      const same = safeArray(filters.sakha_ids).map(Number).includes(Number(profile.sakha_id));
      if (same) return false;
    }

    if (safeArray(filters.sampraday_ids).length) {
      const same = safeArray(filters.sampraday_ids).map(Number).includes(Number(profile.sampraday_id));
      if (same) return false;
    }

    if (safeArray(filters.education_category_ids).length && !safeArray(filters.degree_names).length) {
      const selectedEducationIds = safeArray(filters.education_category_ids).map(Number);
      const profileEducationIds = safeArray(profile.education_category_ids_cache).length
        ? safeArray(profile.education_category_ids_cache).map(Number).filter(Boolean)
        : [Number(profile.education_category_id)].filter(Boolean);
      const same = selectedEducationIds.some((id) => profileEducationIds.includes(id));
      if (same) return false;
    }

    if (safeArray(filters.degree_names).length) {
      const selectedDegreeNames = new Set(
        safeArray(filters.degree_names).map((value) => String(value || "").trim().toLowerCase()).filter(Boolean)
      );
      const profileDegreeNames = safeArray(profile.degree_names_cache)
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean);
      if (profile.education_summary) profileDegreeNames.push(String(profile.education_summary).trim().toLowerCase());
      if (profile.degree_name) profileDegreeNames.push(String(profile.degree_name).trim().toLowerCase());

      const same = profileDegreeNames.some((name) => selectedDegreeNames.has(name));
      if (same) return false;
    }

    if (safeArray(filters.occupation_type_ids).length) {
      const selectedLabels = safeArray(filters.occupation_type_ids)
        .map((id) => getLookupLabel(optionMaps.occupationTypes, id))
        .filter(Boolean);
      const same = selectedLabels.some((label) => sameText(profile.occupation_type, label));
      if (same) return false;
    }

    if (safeArray(filters.tag_ids).length) {
      const selectedTagIds = safeArray(filters.tag_ids).map(Number);
      const cache = Array.isArray(profile.tag_ids_cache) ? profile.tag_ids_cache.map(Number) : [];
      const tagObjects = safeArray(profile.tags).map((t) => Number(t.id || t.tag_id));
      const hasTag = selectedTagIds.some((tagId) => cache.includes(tagId) || tagObjects.includes(tagId));
      if (hasTag) return false;
    }

    return true;
  });
}

function uniqueBy(items, key) {
  const seen = new Set();
  const out = [];

  items.forEach((item) => {
    const value = item[key];
    if (value === null || value === undefined || value === "" || seen.has(String(value))) return;
    seen.add(String(value));
    out.push(item);
  });

  return out;
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function omitUnsupportedNonPreferredFilters(filters) {
  return {
    ...(filters || {}),
    q: "",
    gender_id: "",
    birth_date: "",
    birth_year_from: "",
    birth_year_to: "",
    height_cm_from: "",
    height_cm_to: "",
    tag_ids: [],
  };
}

function sanitizeNonPreferredFilters(filters, preferredFilters) {
  const base = omitUnsupportedNonPreferredFilters(filters);
  const preferred = preferredFilters || {};
  const filterNumericValues = (values, blockedValues) => {
    const blocked = new Set(safeArray(blockedValues).map(Number).filter(Boolean));
    return safeArray(values).filter((value) => !blocked.has(Number(value)));
  };
  const filterTextValues = (values, blockedValues) => {
    const blocked = new Set(
      safeArray(blockedValues)
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean)
    );
    return safeArray(values).filter(
      (value) => !blocked.has(String(value || "").trim().toLowerCase())
    );
  };

  return {
    ...base,
    marital_status_id:
      String(base.marital_status_id || "").trim() &&
      String(base.marital_status_id || "").trim() === String(preferred.marital_status_id || "").trim()
        ? ""
        : base.marital_status_id,
    caste_ids: filterNumericValues(base.caste_ids, preferred.caste_ids),
    sakha_ids: filterNumericValues(base.sakha_ids, preferred.sakha_ids),
    sampraday_ids: filterNumericValues(base.sampraday_ids, preferred.sampraday_ids),
    education_category_ids: filterNumericValues(base.education_category_ids, preferred.education_category_ids),
    degree_names: filterTextValues(base.degree_names, preferred.degree_names),
    occupation_type_ids: filterNumericValues(base.occupation_type_ids, preferred.occupation_type_ids),
    address_filters: sanitizeAddressFilters(base.address_filters, preferred.address_filters),
  };
}

function normalizeSelectValue(value) {
  return String(value ?? "").trim();
}

function orderSelectedOptionsFirst(options, isSelected) {
  const selectedOptions = [];
  const remainingOptions = [];

  safeArray(options).forEach((option) => {
    if (isSelected(option)) {
      selectedOptions.push(option);
    } else {
      remainingOptions.push(option);
    }
  });

  return [...selectedOptions, ...remainingOptions];
}

function removeValueWithCascade(filter, key, removeValue) {
  const nextFilter = {
    ...filter,
    [key]: safeArray(filter?.[key]).filter((value) => Number(value) !== Number(removeValue)),
  };

  if (key === "country_ids") {
    nextFilter.state_ids = [];
    nextFilter.district_ids = [];
  }

  if (key === "state_ids") {
    nextFilter.district_ids = [];
  }

  return nextFilter;
}

function buildNextFiltersAfterChipRemoval(prev, chip, defaultMaritalStatusId = "", degreeNames = []) {
  switch (chip.key) {
    case "q":
    case "gender_id":
      return { ...prev, [chip.key]: "" };
    case "birth_date":
      return { ...prev, birth_date: "" };
    case "birth_year_range":
      return { ...prev, birth_year_from: "", birth_year_to: "" };
    case "height_range":
      return { ...prev, height_cm_from: "", height_cm_to: "" };
    case "marital_status_id":
      return {
        ...prev,
        marital_status_id: chip.source === "filters" ? String(defaultMaritalStatusId || "") : "",
      };
    case "address_filter":
      return {
        ...prev,
        address_filters: safeArray(prev.address_filters).filter((filter) => filter.client_key !== chip.clientKey),
      };
    case "address_filter_value":
      return {
        ...prev,
        address_filters: safeArray(prev.address_filters).map((filter) =>
          filter.client_key !== chip.clientKey
            ? filter
            : removeValueWithCascade(filter, chip.addressKey, chip.removeValue)
        ),
      };
    case "education_category_ids":
      if (chip.removeValue == null) {
        return {
          ...prev,
          education_category_ids: [],
          education_status_by_id: {},
        };
      }
      {
        const nextEducationIds = safeArray(prev.education_category_ids).filter(
          (value) => Number(value) !== Number(chip.removeValue)
        );
        return {
          ...prev,
          education_category_ids: nextEducationIds,
          education_status_by_id: Object.fromEntries(
            Object.entries(prev.education_status_by_id || {}).filter(
              ([educationId]) => Number(educationId) !== Number(chip.removeValue)
            )
          ),
          degree_names: filterDegreeNamesByEducationSelection(
            prev.degree_names,
            degreeNames,
            nextEducationIds
          ),
        };
      }
    case "caste_ids":
      if (chip.removeValue == null) {
        return {
          ...prev,
          caste_ids: [],
          sakha_ids: [],
          non_preferred_sakha_ids: [],
        };
      }
      return {
        ...prev,
        caste_ids: safeArray(prev.caste_ids).filter((value) => Number(value) !== Number(chip.removeValue)),
        sakha_ids: [],
        non_preferred_sakha_ids: [],
      };
    case "degree_names":
      if (chip.removeValue == null) {
        return {
          ...prev,
          degree_names: [],
        };
      }
      return {
        ...prev,
        degree_names: safeArray(prev.degree_names).filter(
          (value) => String(value).trim() !== String(chip.removeValue).trim()
        ),
      };
    case "sakha_ids":
    case "sampraday_ids":
    case "non_preferred_sakha_ids":
    case "occupation_type_ids":
    case "tag_ids":
      if (chip.removeValue == null) {
        return {
          ...prev,
          [chip.key]: [],
        };
      }
      return {
        ...prev,
        [chip.key]: safeArray(prev[chip.key]).filter((value) => Number(value) !== Number(chip.removeValue)),
      };
    default:
      return prev;
  }
}

function buildAddressBlockedValueSets(filters) {
  const blockedCountryIds = new Set();
  const blockedStateIds = new Set();
  const blockedDistrictIds = new Set();

  safeArray(filters).forEach((filter) => {
    safeArray(filter?.country_ids).map(Number).filter(Boolean).forEach((id) => blockedCountryIds.add(id));
    safeArray(filter?.state_ids).map(Number).filter(Boolean).forEach((id) => blockedStateIds.add(id));
    safeArray(filter?.district_ids).map(Number).filter(Boolean).forEach((id) => blockedDistrictIds.add(id));
  });

  return {
    blockedCountryIds,
    blockedStateIds,
    blockedDistrictIds,
  };
}

function sanitizeAddressFilters(filters, preferredAddressFilters) {
  const { blockedCountryIds, blockedStateIds, blockedDistrictIds } = buildAddressBlockedValueSets(preferredAddressFilters);

  return safeArray(filters).map((rawFilter) => {
    let nextFilter = {
      ...rawFilter,
      country_ids: safeArray(rawFilter?.country_ids).map(Number).filter(Boolean),
      state_ids: safeArray(rawFilter?.state_ids).map(Number).filter(Boolean),
      district_ids: safeArray(rawFilter?.district_ids).map(Number).filter(Boolean),
    };

    safeArray(nextFilter.country_ids)
      .filter((value) => blockedCountryIds.has(Number(value)))
      .forEach((value) => {
        nextFilter = removeValueWithCascade(nextFilter, "country_ids", value);
      });
    safeArray(nextFilter.state_ids)
      .filter((value) => blockedStateIds.has(Number(value)))
      .forEach((value) => {
        nextFilter = removeValueWithCascade(nextFilter, "state_ids", value);
      });
    safeArray(nextFilter.district_ids)
      .filter((value) => blockedDistrictIds.has(Number(value)))
      .forEach((value) => {
        nextFilter = removeValueWithCascade(nextFilter, "district_ids", value);
      });

    return nextFilter;
  });
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function normalizePositiveInteger(value) {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return String(Math.round(parsed));
}

function cmValueToFeetInches(value) {
  const normalizedCm = normalizePositiveInteger(value);
  if (!normalizedCm) {
    return { feet: "", inches: "" };
  }

  const totalInches = Math.round(Number(normalizedCm) / 2.54);
  return {
    feet: String(Math.floor(totalInches / 12)),
    inches: String(totalInches % 12),
  };
}

function formatHeightCmAsImperial(value) {
  const imperial = cmValueToFeetInches(value);
  if (!imperial.feet) return "Any";
  return `${imperial.feet} ft ${imperial.inches} in`;
}

function buildHeightRangeOptions() {
  const options = [];
  for (let feet = 4; feet <= 6; feet += 1) {
    for (let inches = 0; inches <= 11; inches += 1) {
      if (feet === 6 && inches > 11) break;
      options.push({
        value: String(Math.round(((feet * 12) + inches) * 2.54)),
        label: `${feet} ft ${inches} inch`,
      });
    }
  }
  return options;
}

function normalizeHeightDropdownValue(value) {
  const normalizedCm = normalizePositiveInteger(value);
  if (!normalizedCm) return "";

  const exact = HEIGHT_RANGE_OPTIONS.find((option) => option.value === normalizedCm);
  if (exact) return exact.value;

  const totalInches = clamp(Math.round(Number(normalizedCm) / 2.54), 48, 83);
  return String(Math.round(totalInches * 2.54));
}

function getNormalizedHeightRange(filters) {
  const from = Number(normalizePositiveInteger(filters?.height_cm_from));
  const to = Number(normalizePositiveInteger(filters?.height_cm_to));

  if (!Number.isFinite(from) && !Number.isFinite(to)) {
    return { from: "", to: "" };
  }

  if (Number.isFinite(from) && Number.isFinite(to) && from > to) {
    return { from: String(to), to: String(from) };
  }

  return {
    from: Number.isFinite(from) ? String(from) : "",
    to: Number.isFinite(to) ? String(to) : "",
  };
}

function formatHeightRangeSummary(filters) {
  const normalized = getNormalizedHeightRange(filters);
  if (normalized.from && normalized.to) {
    return `${formatHeightCmAsImperial(normalized.from)} - ${formatHeightCmAsImperial(normalized.to)}`;
  }
  if (normalized.from) {
    return `From ${formatHeightCmAsImperial(normalized.from)}`;
  }
  if (normalized.to) {
    return `Up to ${formatHeightCmAsImperial(normalized.to)}`;
  }
  return "";
}

function filterDegreeNamesByEducationSelection(selectedDegreeNames, degreeItems, educationCategoryIds) {
  const normalizedDegrees = safeArray(selectedDegreeNames).map((value) => String(value || "").trim()).filter(Boolean);
  const normalizedItems = safeArray(degreeItems);
  const selectedEducationIds = safeArray(educationCategoryIds).map(Number).filter(Boolean);
  const hasCategoryMapping = normalizedItems.some((item) => Number(item?.education_category_id) > 0);

  if (!hasCategoryMapping) return normalizedDegrees;
  if (!selectedEducationIds.length) return [];

  const allowedDegreeSet = new Set(
    normalizedItems
      .filter((item) => selectedEducationIds.includes(Number(item?.education_category_id)))
      .map((item) => String(item?.name || item?.display_label || "").trim())
      .filter(Boolean)
  );

  return normalizedDegrees.filter((value) => allowedDegreeSet.has(value));
}

function buildInitialFilters(maritalStatusOptions = []) {
  const singleOption = safeArray(maritalStatusOptions).find((item) => {
    const label = String(item?.name || item?.display_label || "").trim().toLowerCase();
    return label === "single" || label === "never married";
  });

  return {
    ...DEFAULT_FILTERS,
    caste_ids: [],
    sakha_ids: [],
    sampraday_ids: [],
    non_preferred_sakha_ids: [],
    education_category_ids: [],
    degree_names: [],
    education_status_by_id: {},
    occupation_type_ids: [],
    tag_ids: [],
    address_filters: [],
    marital_status_id: singleOption?.id ? String(singleOption.id) : "",
  };
}

function buildInitialNonPreferredFilters() {
  return {
    ...GLOBAL_PREFERENCE_FILTER_DEFAULTS,
    address_filters: [],
    caste_ids: [],
    sakha_ids: [],
    sampraday_ids: [],
    non_preferred_sakha_ids: [],
    education_category_ids: [],
    degree_names: [],
    education_status_by_id: {},
    occupation_type_ids: [],
    tag_ids: [],
  };
}

function buildInitialNonVerifiedFilters() {
  return {
    ...NON_VERIFIED_FILTER_DEFAULTS,
  };
}

function normalizeEducationStatusValue(value) {
  if (!value) return "";
  return String(value).trim().toLowerCase() === "pursuing" ? "pursuing" : "completed";
}

function getEducationPairFilters(filters) {
  if (safeArray(filters?.degree_names).length) return [];
  return safeArray(filters?.education_category_ids).map((educationId) => ({
    education_category_id: Number(educationId),
    education_status: normalizeEducationStatusValue(filters?.education_status_by_id?.[educationId]) || "completed",
  })).filter((item) => item.education_category_id > 0);
}

function countAppliedFilters(filters, defaultMaritalStatusId = "") {
  let count = 0;

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (key.endsWith("_not_in")) return;
    if (key === "birth_year_from" || key === "birth_year_to") return;
    if (key === "birth_date") return;
    if (key === "height_cm_from" || key === "height_cm_to") return;
    if (key === "education_status_by_id") return;
    if (key === "address_filters") return;
    if (key === "search_by") return;
    if (key === "non_preferred_sakha_ids") return;
    if (key === "education_category_ids" && safeArray(filters?.degree_names).length) return;
    if (key === "marital_status_id" && defaultMaritalStatusId && String(value || "") === defaultMaritalStatusId) return;

    if (Array.isArray(value)) {
      if (value.length) count += 1;
      return;
    }

    if (value !== "" && value !== null && value !== undefined && value !== false) {
      count += 1;
    }
  });

  if (filters?.birth_year_from || filters?.birth_year_to) count += 1;
  if (filters?.birth_date) count += 1;
  if (filters?.height_cm_from || filters?.height_cm_to) count += 1;
  count += getActiveAddressFilters(filters?.address_filters).length;

  return count;
}

function countNonVerifiedFilters(filters) {
  return hasSearchFilterValue(filters) ? 1 : 0;
}

function pushPreferenceChips({ chips, filters, lookups, locationMaps, defaultMaritalStatusId = "", source = "filters", notIn = false }) {
  const pushChip = (label, value, meta = {}) => {
    if (!value && value !== 0) return;
    chips.push({ label, value, notIn, source, ...meta });
  };

  const pushIdArrayChips = (label, key, values, items) => {
    safeArray(values).forEach((selectedId) => {
      pushChip(label, lookupById(items, selectedId), {
        key,
        removeValue: Number(selectedId),
      });
    });
  };

  const pushTextArrayChips = (label, key, values) => {
    safeArray(values)
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .forEach((selectedValue) => {
        pushChip(label, selectedValue, {
          key,
          removeValue: selectedValue,
        });
      });
  };

  if (filters.q) {
    pushChip(getSearchModeLabel(filters.search_by), filters.q, { key: "q" });
  }

  if (filters.gender_id) {
    pushChip("Gender", lookupById(lookups.genders, filters.gender_id), { key: "gender_id" });
  }

  if (filters.birth_date) {
    pushChip("DOB", formatFilterDate(filters.birth_date), { key: "birth_date" });
  }

  if (filters.birth_year_from || filters.birth_year_to) {
    pushChip("Birth Year", `${filters.birth_year_from || "Any"}-${filters.birth_year_to || "Any"}`, { key: "birth_year_range" });
  }

  if (filters.height_cm_from || filters.height_cm_to) {
    pushChip("Height", formatHeightRangeSummary(filters), { key: "height_range" });
  }

  safeArray(filters.address_filters).forEach((filter) => {
    const normalizedFilter = {
      client_key: filter.client_key || null,
      address_type: String(filter.address_type || "").trim().toLowerCase(),
      country_ids: safeArray(filter.country_ids).map(Number).filter(Boolean),
      state_ids: safeArray(filter.state_ids).map(Number).filter(Boolean),
      district_ids: safeArray(filter.district_ids).map(Number).filter(Boolean),
    };

    if (
      normalizedFilter.address_type &&
      (
        normalizedFilter.country_ids.length ||
        normalizedFilter.state_ids.length ||
        normalizedFilter.district_ids.length
      )
    ) {
      const addressTypeLabel = labelizeAddressType(normalizedFilter.address_type);
      normalizedFilter.country_ids.forEach((id) => {
        const countryName = getCountryNameById(id, locationMaps);
        if (countryName) {
          pushChip(`${addressTypeLabel} Country`, countryName, {
            key: "address_filter_value",
            clientKey: normalizedFilter.client_key,
            addressKey: "country_ids",
            removeValue: id,
          });
        }
      });
      normalizedFilter.state_ids.forEach((id) => {
        const stateName = locationMaps.statesById.get(Number(id))?.name;
        if (stateName) {
          pushChip(`${addressTypeLabel} State`, stateName, {
            key: "address_filter_value",
            clientKey: normalizedFilter.client_key,
            addressKey: "state_ids",
            removeValue: id,
          });
        }
      });
      normalizedFilter.district_ids.forEach((id) => {
        const districtName = locationMaps.districtsById.get(Number(id))?.name;
        if (districtName) {
          pushChip(`${addressTypeLabel} District`, districtName, {
            key: "address_filter_value",
            clientKey: normalizedFilter.client_key,
            addressKey: "district_ids",
            removeValue: id,
          });
        }
      });
    }
  });

  if (filters.marital_status_id && (!defaultMaritalStatusId || String(filters.marital_status_id) !== defaultMaritalStatusId)) {
    pushChip("Marital", lookupById(lookups.maritalStatus, filters.marital_status_id), { key: "marital_status_id" });
  }

  pushIdArrayChips("Caste", "caste_ids", filters.caste_ids, lookups.castes);
  pushIdArrayChips("Sakha", "sakha_ids", filters.sakha_ids, lookups.sakhas);
  pushIdArrayChips("Sampraday", "sampraday_ids", filters.sampraday_ids, lookups.sampradays);
  if (safeArray(filters.education_category_ids).length && !safeArray(filters.degree_names).length) {
    getEducationPairFilters(filters).forEach((item) => {
      const educationName = lookupById(lookups.educationCategories, item.education_category_id) || `#${item.education_category_id}`;
      pushChip("Education", `${educationName} (${labelizeEducationStatus(item.education_status)})`, {
        key: "education_category_ids",
        removeValue: Number(item.education_category_id),
      });
    });
  }

  pushTextArrayChips("Degree", "degree_names", filters.degree_names);
  pushIdArrayChips("Occupation", "occupation_type_ids", filters.occupation_type_ids, lookups.occupationTypes);
  pushIdArrayChips("Tag", "tag_ids", filters.tag_ids, lookups.tags);
}

function pushNonVerifiedChips(chips, filters) {
  const mode = normalizeSearchMode(filters?.search_by);
  const query = String(filters?.q || "").trim();
  const birthDate = String(filters?.birth_date || "").trim();

  if (isDobSearchMode(mode) && birthDate) {
    chips.push({
      label: "DOB",
      value: formatFilterDate(birthDate),
      notIn: false,
      source: "nonVerifiedFilters",
      key: "birth_date",
    });
    return;
  }

  if (!query) return;
  chips.push({
    label: getSearchModeLabel(mode),
    value: query,
    notIn: false,
    source: "nonVerifiedFilters",
    key: "q",
  });
}

function getActiveAddressFilters(filters) {
  return safeArray(filters)
    .map((filter) => ({
      client_key: filter.client_key || null,
      address_type: String(filter.address_type || "").trim().toLowerCase(),
      country_ids: safeArray(filter.country_ids).map(Number).filter(Boolean),
      state_ids: safeArray(filter.state_ids).map(Number).filter(Boolean),
      district_ids: safeArray(filter.district_ids).map(Number).filter(Boolean),
    }))
    .filter((filter) =>
      filter.address_type &&
      (
        filter.country_ids.length ||
        filter.state_ids.length ||
        filter.district_ids.length
      )
    )
    .map(({ client_key, ...rest }) => rest);
}

function profileMatchesAddressFilter(profile, filter, locationMaps) {
  const rows = safeArray(profile.address_filters_cache);
  const fallbackGeo = getProfileGeo(profile, locationMaps);
  const candidates = rows.length
    ? rows.map((row) => ({
        address_type: String(row.address_type || "").trim().toLowerCase(),
        country_id: Number(row.country_id || 0),
        state_id: Number(row.state_id || 0),
        district_id: Number(row.district_id || 0),
      }))
    : [{
        address_type: "current",
        country_id: Number(fallbackGeo.country_id || 0),
        state_id: Number(fallbackGeo.state_id || 0),
        district_id: Number(fallbackGeo.district_id || 0),
      }];

  return candidates.some((row) => {
    if (filter.address_type && row.address_type && row.address_type !== filter.address_type) return false;
    if (filter.country_ids.length && !filter.country_ids.includes(row.country_id)) return false;
    if (filter.state_ids.length && !filter.state_ids.includes(row.state_id)) return false;
    if (filter.district_ids.length && !filter.district_ids.includes(row.district_id)) return false;
    return true;
  });
}

function buildAddressFilterOptions(filter, locations, blockedValues = null) {
  const rows = safeArray(locations);
  const selectedCountryIds = safeArray(filter.country_ids).map(Number).filter(Boolean);
  const selectedStateIds = safeArray(filter.state_ids).map(Number).filter(Boolean);
  const blockedCountryIds = blockedValues?.blockedCountryIds || new Set();
  const blockedStateIds = blockedValues?.blockedStateIds || new Set();
  const blockedDistrictIds = blockedValues?.blockedDistrictIds || new Set();

  const countries = uniqueBy(
    rows
      .map((location) => ({
        id: location.country_id || location.countryId || location.countryID,
        label: getCountryName(location),
      }))
      .filter((option) => option.id && option.label && !blockedCountryIds.has(Number(option.id))),
    "id"
  ).sort((a, b) => String(a.label).localeCompare(String(b.label)));

  const states = uniqueBy(
    rows
      .filter((location) => !selectedCountryIds.length || selectedCountryIds.includes(Number(location.country_id || location.countryId || location.countryID)))
      .map((location) => ({
        id: location.state_id || location.stateId || location.stateID,
        label: location.state || location.state_name || location.stateName || "Unknown State",
      }))
      .filter((option) => option.id && option.label && !blockedStateIds.has(Number(option.id))),
    "id"
  ).sort((a, b) => String(a.label).localeCompare(String(b.label)));

  const districts = uniqueBy(
    rows
      .filter((location) => !selectedCountryIds.length || selectedCountryIds.includes(Number(location.country_id || location.countryId || location.countryID)))
      .filter((location) => !selectedStateIds.length || selectedStateIds.includes(Number(location.state_id || location.stateId || location.stateID)))
      .map((location) => ({
        id: location.district_id || location.districtId || location.districtID,
        label: location.district || location.district_name || location.districtName || "Unknown District",
      }))
      .filter((option) => option.id && option.label && !blockedDistrictIds.has(Number(option.id))),
    "id"
  ).sort((a, b) => String(a.label).localeCompare(String(b.label)));

  return { countries, states, districts };
}

function labelizeAddressType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ADDRESS_TYPE_OPTIONS.find((option) => option.value === normalized)?.label || "Address";
}

function getCountryNameById(countryId, locationMaps) {
  for (const location of locationMaps.byCityId.values()) {
    if (Number(location.country_id || location.countryId || location.countryID) === Number(countryId)) {
      return getCountryName(location);
    }
  }
  return "";
}

function labelizeEducationStatus(value) {
  const normalized = normalizeEducationStatusValue(value);
  if (!normalized) return "";
  return normalized === "pursuing" ? "Pursuing" : "Completed";
}

function labelizePersonaTier(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getFullName(profile) {
  return (
    profile.full_name ||
    [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" ") ||
    "Unnamed Profile"
  );
}

function getAge(dateValue) {
  if (!dateValue) return null;
  const dob = new Date(dateValue);
  if (Number.isNaN(dob.getTime())) return null;
  return Math.floor((new Date() - dob) / (365.25 * 86400000));
}

function formatFilterDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB");
}

function truncate(value, max) {
  const text = String(value || "");
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

function pickColor(str) {
  const palette = ["#D73C2E", "#B9343A", "#C7433F", "#A93338", "#D1483A", "#252935"];
  let hash = 0;
  for (const char of str) hash = char.charCodeAt(0) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

const C = {
  bg: "#fff9f7",
  card: "#ffffff",
  border: "rgba(185,52,58,0.12)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  blue: "#D73C2E",
  blueLight: "#fff4f1",
  text: "#252935",
  muted: "#6b6f7d",
  inputBg: "#ffffff",
  soft: "#fff9f7",
  green: "#B9343A",
};

const CARD_MEDIA_HEIGHT = 210;

const S = {
  page: {
    minHeight: "calc(100vh - 60px)",
    background: "radial-gradient(circle at 8% 0%, rgba(215,60,46,0.12) 0, transparent 28%), radial-gradient(circle at 92% 10%, rgba(185,52,58,0.10) 0, transparent 24%), linear-gradient(180deg, #fffefc 0%, #fff8f6 52%, #fff9f7 100%)",
    padding: "20px 20px 48px",
    fontFamily: "var(--jm-font-body)",
  },

  layout: {
    display: "block",
    maxWidth: 1440,
    margin: "0 auto",
  },

  sidebar: {
    width: 330,
    flexShrink: 0,
    background: "#ffffff",
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    padding: "24px 20px 20px",
    position: "sticky",
    top: 92,
    boxShadow: "0 20px 60px rgba(230,62,92,0.10)",
    maxHeight: "calc(100vh - 112px)",
    overflowY: "auto",
  },

  sidebarTop: {
    marginBottom: 14,
    paddingBottom: 14,
    borderBottom: `1px solid ${C.border}`,
  },

  eyebrow: {
    fontSize: 11,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: C.accent,
    margin: "0 0 6px",
    fontWeight: 950,
  },

  sidebarTitle: {
    fontSize: 26,
    fontFamily: "var(--jm-font-heading)",
    fontWeight: 700,
    color: C.text,
    margin: 0,
    lineHeight: 1.1,
    letterSpacing: -0.55,
  },

  sidebarSub: {
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.55,
    margin: "8px 0 0",
  },

  filterGroup: { marginBottom: 17 },

  addressSectionHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 12,
  },

  addAddressFilterBtn: {
    minHeight: 40,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid rgba(230,62,92,0.18)",
    background: "#fff1f4",
    color: C.accentDark,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 950,
    flexShrink: 0,
  },

  addressEmptyState: {
    padding: 14,
    borderRadius: 18,
    border: "1px dashed rgba(230,62,92,0.22)",
    background: "#fdf7f7",
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
  },

  addressFilterStack: {
    display: "grid",
    gap: 12,
  },

  addressFilterCard: {
    padding: 16,
    borderRadius: 22,
    border: "1px solid rgba(230,62,92,0.14)",
    background: "#fff",
    boxShadow: "0 12px 30px rgba(230,62,92,0.08)",
  },

  addressFilterCardTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },

  addressCollapseBtn: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
  },

  addressCollapseIcon: {
    width: 30,
    height: 30,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    background: "#fff1f4",
    color: C.accentDark,
    fontSize: 18,
    fontWeight: 900,
    flexShrink: 0,
  },

  addressFilterCardTitle: {
    margin: 0,
    color: C.text,
    fontSize: 16,
    fontWeight: 950,
  },

  addressRemoveBtn: {
    minHeight: 34,
    padding: "0 14px",
    borderRadius: 999,
    border: "1px solid rgba(230,62,92,0.16)",
    background: "#fff",
    color: C.muted,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    flexShrink: 0,
  },

  addressFilterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "14px 16px",
  },

  filterLabelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 7,
  },

  filterLabel: {
    display: "block",
    fontSize: 10,
    fontWeight: 950,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: C.accentDark,
    marginBottom: 7,
  },

  filterHint: {
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: -2,
  },

  preferenceTabs: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 7,
  },

  preferenceTab: {
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.muted,
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",
  },

  preferenceTabActive: {
    background: C.accentLight,
    color: C.accent,
    borderColor: C.accent,
  },

  preferenceTabDisabled: {
    opacity: 0.42,
    cursor: "not-allowed",
  },

  notInHint: {
    marginTop: 6,
    color: C.accentDark,
    background: C.accentLight,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: "6px 9px",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.4,
  },
  filterPhoneInputWrap: {
    gap: 0,
  },
  filterPhoneSelectWrap: {
    minWidth: 0,
  },
  filterPhoneSelect: {
    width: "100%",
    minHeight: 42,
    padding: "0 12px",
    border: "1px solid rgba(230,62,92,0.18)",
    borderRadius: 14,
    background: C.inputBg,
    color: C.text,
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
  },

  input: {
    width: "100%",
    minHeight: 42,
    padding: "0 12px",
    border: "1px solid rgba(230,62,92,0.18)",
    borderRadius: 14,
    background: C.inputBg,
    color: C.text,
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
  },

  radioRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginTop: 10,
    flexWrap: "wrap",
  },

  radioOption: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },

  radioInput: {
    accentColor: C.accent,
    cursor: "pointer",
  },

  select: {
    width: "100%",
    minHeight: 42,
    padding: "0 12px",
    border: "1px solid rgba(230,62,92,0.18)",
    borderRadius: 14,
    background: C.inputBg,
    color: C.text,
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
  },

  ageRow: { display: "flex", alignItems: "center", gap: 8 },

  ageInput: {
    flex: 1,
    minWidth: 0,
    minHeight: 42,
    padding: "0 10px",
    border: "1px solid rgba(230,62,92,0.18)",
    borderRadius: 14,
    background: C.inputBg,
    color: C.text,
    fontSize: 14,
    boxSizing: "border-box",
    textAlign: "center",
    outline: "none",
  },

  ageDash: { color: C.muted, fontSize: 16, flexShrink: 0 },

  heightRangeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },

  heightRangeField: {
    display: "grid",
    gap: 6,
    minWidth: 0,
  },

  heightRangeLabel: {
    color: C.text,
    fontSize: 13,
    fontWeight: 900,
  },

  heightRangeSelect: {
    width: "100%",
    minHeight: 42,
    padding: "0 12px",
    border: "1px solid rgba(230,62,92,0.18)",
    borderRadius: 12,
    background: "#fff",
    color: C.text,
    fontSize: 13,
    boxSizing: "border-box",
    outline: "none",
  },

  multiDropdown: {
    border: "1px solid rgba(230,62,92,0.16)",
    borderRadius: 18,
    background: "#fff",
    overflow: "hidden",
  },

  multiDropdownDisabled: {
    opacity: 0.7,
  },

  multiDropdownSummary: {
    listStyle: "none",
    cursor: "pointer",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
  },

  multiDropdownSummaryDisabled: {
    cursor: "not-allowed",
  },

  multiDropdownCount: {
    minWidth: 24,
    height: 24,
    padding: "0 7px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    background: "#fff1f4",
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
  },

  multiDropdownMenu: {
    display: "grid",
    gap: 10,
    padding: "0 14px 14px",
    borderTop: "1px solid rgba(230,62,92,0.10)",
  },

  multiDropdownOptions: {
    display: "grid",
    gap: 10,
    maxHeight: 220,
    overflowY: "auto",
    paddingRight: 4,
  },

  multiDropdownEmpty: {
    paddingTop: 10,
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
  },

  multiDropdownSearch: {
    marginTop: 12,
    minHeight: 40,
    borderRadius: 12,
    border: "1px solid rgba(230,62,92,0.16)",
    background: "#fff",
    padding: "0 12px",
    color: C.text,
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
  },

  checkboxOption: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(230,62,92,0.14)",
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 800,
  },

  checkboxOptionActive: {
    background: "#fff1f4",
    borderColor: "#D73C2E",
    boxShadow: "0 8px 18px rgba(230,62,92,0.10)",
  },

  checkboxOptionActiveAlt: {
    background: "#fff8f8",
    borderColor: "#b23b3f",
    boxShadow: "0 8px 18px rgba(230,62,92,0.08)",
  },

  educationOptionActive: {
    justifyContent: "space-between",
    gap: 12,
  },

  educationOptionLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    flex: 1,
    minWidth: 0,
  },

  educationStatusBtn: {
    minHeight: 30,
    padding: "0 12px",
    borderRadius: 999,
    border: "1px solid rgba(230,62,92,0.22)",
    background: "#fff",
    color: C.muted,
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",
    flexShrink: 0,
  },

  educationStatusBtnActive: {
    background: "#D73C2E",
    borderColor: "#D73C2E",
    color: "#fff",
    boxShadow: "0 8px 18px rgba(230,62,92,0.14)",
  },

  checkboxInput: {
    width: 16,
    height: 16,
    accentColor: "#D73C2E",
    margin: 0,
    flexShrink: 0,
  },

  multiSelectWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  multiSelectChip: {
    padding: "8px 12px",
    borderRadius: 999,
    border: "1px solid rgba(230,62,92,0.18)",
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
  },

  multiSelectChipActive: {
    background: "#D73C2E",
    color: "#fff",
    borderColor: "#D73C2E",
    boxShadow: "0 10px 22px rgba(230,62,92,0.18)",
  },

  multiSelectChipActiveAlt: {
    background: "#fff1f4",
    color: "#D73C2E",
    borderColor: "#D73C2E",
    boxShadow: "0 10px 22px rgba(230,62,92,0.12)",
  },


  filterFab: {
    position: "fixed",
    right: 28,
    bottom: 28,
    zIndex: 80,
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    minHeight: 54,
    padding: "0 20px",
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: "#fff",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 950,
    boxShadow: "0 18px 46px rgba(230,62,92,0.28)",
  },

  filterFabIcon: {
    fontSize: 18,
    lineHeight: 1,
  },

  filterFabBadge: {
    minWidth: 24,
    height: 24,
    padding: "0 7px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    background: "#fff",
    color: C.accent,
    fontSize: 12,
    fontWeight: 950,
    boxShadow: "0 6px 18px rgba(230,62,92,0.16)",
  },

  filterOverlayBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 120,
    background: "rgba(47,17,19,0.54)",
    backdropFilter: "blur(8px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "28px 18px",
    overflowY: "auto",
    animation: "overlayFade .18s ease both",
  },

  filterOverlayCard: {
    width: "min(1060px, 100%)",
    background: "#fff",
    borderRadius: 30,
    border: "1px solid rgba(230,62,92,0.16)",
    boxShadow: "0 30px 90px rgba(47,17,19,0.28)",
    animation: "drawerUp .22s ease both",
    overflow: "hidden",
  },

  filterPanelContent: {
    padding: 24,
  },

  filterPanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
    alignItems: "flex-start",
    paddingBottom: 18,
    borderBottom: "1px solid rgba(230,62,92,0.14)",
    marginBottom: 16,
  },

  filterOverlayEyebrow: {
    margin: 0,
    color: C.accent,
    fontSize: 11,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    fontWeight: 950,
  },

  filterOverlayTitle: {
    margin: "6px 0 0",
    color: C.text,
    fontSize: 30,
    lineHeight: 1.1,
    fontWeight: 950,
    letterSpacing: -0.8,
  },

  filterOverlaySub: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 680,
  },

  filterCloseBtn: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "1px solid rgba(230,62,92,0.16)",
    background: "#fff",
    color: C.text,
    fontSize: 26,
    lineHeight: "38px",
    cursor: "pointer",
    fontWeight: 500,
  },

  filterOverlaySummary: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: 14,
    borderRadius: 20,
    background: "#fdf7f7",
    border: "1px solid rgba(230,62,92,0.14)",
    color: C.muted,
    fontSize: 13,
    fontWeight: 900,
    marginBottom: 18,
  },

  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px 18px",
    maxHeight: "58vh",
    overflowY: "auto",
    paddingRight: 6,
  },

  filterOverlayFooter: {
    position: "sticky",
    bottom: 0,
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    paddingTop: 18,
    marginTop: 18,
    background: "#fff",
    borderTop: "1px solid rgba(230,62,92,0.14)",
  },

  applyFilterBtn: {
    minHeight: 44,
    padding: "0 22px",
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: "#fff",
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 950,
    boxShadow: "0 14px 30px rgba(230,62,92,0.20)",
  },

  sidebarQuickPanel: {
    position: "sticky",
    top: 0,
    zIndex: 3,
    marginBottom: 18,
    padding: 12,
    borderRadius: 22,
    background: "#ffffff",
    border: "1px solid rgba(230,62,92,0.14)",
    boxShadow: "0 14px 34px rgba(15,23,42,0.06)",
  },

  topFilterSummary: {
    padding: "10px 12px",
    borderRadius: 18,
    background: "#fdf7f7",
    border: "1px solid rgba(230,62,92,0.14)",
    color: C.muted,
    fontSize: 12,
    fontWeight: 900,
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
  },

  sidebarActionsTop: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
  },

  dismissBtn: {
    width: "100%",
    minHeight: 40,
    background: C.text,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
  },

  filtersPanel: {
    animation: "fadeUp 0.18s ease both",
  },

  sidebarActions: { marginTop: 24, display: "flex", flexDirection: "column", gap: 9 },

  searchBtn: {
    width: "100%",
    minHeight: 44,
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: "#fff",
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 950,
    letterSpacing: 0.2,
    boxShadow: "0 14px 30px rgba(230,62,92,0.20)",
  },

  clearBtn: {
    width: "100%",
    minHeight: 40,
    background: "#fff",
    color: C.muted,
    border: "1px solid rgba(230,62,92,0.18)",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
  },

  filterSummary: {
    marginTop: 14,
    padding: 12,
    borderRadius: 18,
    background: "#fdf7f7",
    border: "1px solid rgba(230,62,92,0.14)",
    color: C.muted,
    fontSize: 12,
    fontWeight: 850,
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
  },

  main: { minHeight: 500, minWidth: 0, width: "100%" },

  hero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    padding: "28px 30px",
    borderRadius: 30,
    background: "linear-gradient(135deg, rgba(255,255,255,0.99) 0%, rgba(255,246,243,0.97) 62%, rgba(255,250,247,0.96) 100%)",
    border: "1px solid rgba(230,62,92,0.10)",
    boxShadow: "0 22px 60px rgba(230,62,92,0.10)",
    marginBottom: 16,
  },
  policyNotice: {
    marginBottom: 16,
    border: `1px solid ${C.border}`,
    background: "#fff7f7",
    color: C.text,
    borderRadius: 18,
    padding: "12px 14px",
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  policyNoticeTitle: {
    color: C.accent,
  },

  mainEyebrow: {
    margin: 0,
    fontSize: 11,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: C.accent,
    fontWeight: 950,
  },

  mainTitle: {
    margin: "6px 0 0",
    color: C.text,
    fontSize: 40,
    fontWeight: 700,
    letterSpacing: -0.95,
    lineHeight: 1.04,
  },

  mainSub: {
    margin: "10px 0 0",
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.7,
    maxWidth: 620,
  },
  heroSearchForm: {
    marginTop: 16,
    display: "grid",
    gap: 12,
    maxWidth: 700,
  },
  heroSearchRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  heroPhoneInputWrap: {
    flex: 1,
    minWidth: 320,
  },
  heroPhoneSelectWrap: {
    minWidth: 0,
  },
  heroPhoneSelect: {
    width: "100%",
    minHeight: 48,
    padding: "0 12px",
    border: "1px solid rgba(230,62,92,0.18)",
    borderRadius: 16,
    background: "#fff",
    color: C.text,
    fontSize: 15,
    fontWeight: 700,
    boxSizing: "border-box",
    outline: "none",
    boxShadow: "0 12px 28px rgba(230,62,92,0.08)",
  },
  heroSearchInput: {
    flex: 1,
    minWidth: 260,
    minHeight: 48,
    padding: "0 16px",
    border: "1px solid rgba(230,62,92,0.18)",
    borderRadius: 16,
    background: "#fff",
    color: C.text,
    fontSize: 15,
    fontWeight: 700,
    boxSizing: "border-box",
    outline: "none",
    boxShadow: "0 12px 28px rgba(230,62,92,0.08)",
  },
  heroSearchBtn: {
    minHeight: 48,
    padding: "0 22px",
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg, #D73C2E 0%, #B9343A 100%)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 16px 34px rgba(185,52,58,0.24)",
    whiteSpace: "nowrap",
    alignSelf: "flex-start",
  },
  heroSearchModes: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  heroSearchModeOption: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },

  toolbar: {
    display: "flex",
    gap: 14,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },

  toolbarGroup: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  toolbarLabel: {
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
  },

  toolbarDivider: {
    width: 1,
    height: 34,
    background: "rgba(230,62,92,0.14)",
  },
  toolbarGhostBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 40,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid rgba(230,62,92,0.18)",
    background: "#fff7f5",
    color: C.text,
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  toolbarSelect: {
    height: 40,
    padding: "0 14px",
    borderRadius: 999,
    border: "1px solid rgba(230,62,92,0.18)",
    background: "#fff",
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
    outline: "none",
  },

  viewToggle: {
    display: "flex",
    padding: 4,
    borderRadius: 999,
    background: "#fff",
    border: "1px solid rgba(230,62,92,0.14)",
    boxShadow: "0 8px 24px rgba(230,62,92,0.06)",
  },

  viewToggleBtn: {
    border: "none",
    background: "transparent",
    padding: "8px 13px",
    borderRadius: 999,
    cursor: "pointer",
    color: C.muted,
    fontSize: 12,
    fontWeight: 950,
  },

  viewToggleActive: {
    background: C.accent,
    color: "#fff",
  },

  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 18,
    alignItems: "stretch",
    marginBottom: 18,
  },

  statCard: {
    background: "#ffffff",
    border: "1px solid rgba(230,62,92,0.10)",
    borderRadius: 24,
    padding: "20px 22px",
    boxShadow: "0 16px 40px rgba(230,62,92,0.08)",
    display: "grid",
    gridTemplateColumns: "56px minmax(0, 1fr)",
    alignItems: "center",
    gap: 16,
    width: "100%",
    minHeight: 116,
    textAlign: "left",
    fontFamily: "inherit",
    appearance: "none",
    boxSizing: "border-box",
  },

  statCardStatic: {
    cursor: "default",
  },

  statCardClickable: {
    cursor: "pointer",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
  },

  statBadge: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #fff4f1 0%, #fffaf8 100%)",
    color: C.accent,
    border: "1px solid rgba(230,62,92,0.10)",
    flexShrink: 0,
  },

  statContent: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },

  statValue: {
    color: C.accentDark,
    fontSize: 36,
    fontWeight: 950,
    lineHeight: 1,
  },

  statLabel: {
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.35,
  },

  statHint: {
    color: C.accent,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.35,
  },


  activeFilterBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    padding: "14px 18px",
    borderRadius: 20,
    background: "#ffffff",
    border: "1px solid rgba(230,62,92,0.10)",
    boxShadow: "0 14px 34px rgba(230,62,92,0.06)",
    marginBottom: 16,
  },

  activeFilterContent: {
    width: "100%",
    minWidth: 0,
  },

  activeFilterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 8,
    flexWrap: "wrap",
  },

  activeFilterActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  activeFilterTitle: {
    margin: 0,
    color: C.text,
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 1.4,
  },

  filterInlineBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    minHeight: 42,
    padding: "0 16px",
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: "#fff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
    boxShadow: "0 12px 28px rgba(230,62,92,0.18)",
    whiteSpace: "nowrap",
  },

  filterInlineIcon: {
    fontSize: 16,
    lineHeight: 1,
  },

  filterInlineBadge: {
    minWidth: 22,
    height: 22,
    padding: "0 6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    background: "#fff",
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
  },

  activeFilterChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  activeChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: C.blueLight,
    color: C.blue,
    fontSize: 11,
    fontWeight: 900,
    border: "1px solid #ffd2db",
  },

  excludeChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 11,
    fontWeight: 900,
    border: "1px solid #ffd2db",
  },

  filterChipText: {
    minWidth: 0,
  },

  filterChipRemove: {
    width: 18,
    height: 18,
    marginLeft: 8,
    borderRadius: "50%",
    border: "none",
    background: "rgba(43,49,66,0.14)",
    color: "inherit",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    flexShrink: 0,
  },

  noFilters: {
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
  },

  resetSmallBtn: {
    border: "1px solid rgba(230,62,92,0.16)",
    background: "#fff",
    borderRadius: 999,
    color: C.accent,
    fontSize: 12,
    fontWeight: 900,
    padding: "9px 14px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  errorBox: {
    marginBottom: 14,
    padding: 14,
    borderRadius: 18,
    background: "#fee2e2",
    color: "#b91c1c",
    fontSize: 13,
    fontWeight: 900,
  },

  splash: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 410,
    textAlign: "center",
    background: "#ffffff",
    borderRadius: 28,
    border: "1px dashed rgba(230,62,92,0.24)",
  },

  splashOrb: {
    width: 84,
    height: 84,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #fff2ef, #ffffff)",
    marginBottom: 22,
    animation: "pulse 3s ease-in-out infinite",
  },

  splashTitle: { fontSize: 22, color: C.text, margin: "0 0 8px", fontWeight: 950 },
  splashSub: { fontSize: 14, color: C.muted, margin: "0 0 20px", maxWidth: 520, lineHeight: 1.6 },

  splashClear: {
    background: "#fff",
    border: `1px solid ${C.accent}`,
    color: C.accent,
    borderRadius: 999,
    padding: "9px 20px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
  },

  spinner: {
    width: 38,
    height: 38,
    border: `3px solid ${C.border}`,
    borderTopColor: C.accent,
    borderRadius: "50%",
    marginBottom: 16,
    animation: "spin 0.9s linear infinite",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 20,
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 20,
    padding: "14px 18px",
    borderRadius: 22,
    background: "#ffffff",
    border: "1px solid rgba(230,62,92,0.10)",
    boxShadow: "0 14px 34px rgba(230,62,92,0.06)",
  },

  paginationBtn: {
    border: "none",
    borderRadius: 999,
    background: C.accent,
    color: "#fff",
    padding: "10px 18px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
  },

  paginationBtnDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },

  paginationInfo: {
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
    textAlign: "center",
    flex: 1,
  },

  card: {
    background: "#ffffff",
    border: "1px solid rgba(230,62,92,0.10)",
    borderRadius: 24,
    overflow: "hidden",
    transition: "transform .2s ease, box-shadow .2s ease",
    boxShadow: "0 16px 42px rgba(230,62,92,0.08)",
    animation: "fadeUp 0.28s ease both",
  },
  selectableCard: {
    cursor: "pointer",
  },

  cardHover: {
    transform: "translateY(-4px)",
    boxShadow: "0 24px 58px rgba(230,62,92,0.14)",
  },
  cardSelected: {
    border: "1px solid rgba(215,60,46,0.32)",
    boxShadow: "0 22px 54px rgba(230,62,92,0.16)",
  },

  cardMediaWrap: {
    width: "100%",
    height: CARD_MEDIA_HEIGHT,
    overflow: "hidden",
    flexShrink: 0,
    position: "relative",
    background: "linear-gradient(180deg, rgba(248,233,235,0.95), rgba(255,255,255,0.98))",
  },

  mediaBackdrop: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },

  mediaBackdropImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    filter: "blur(20px) saturate(1.08)",
    transform: "scale(1.08)",
    opacity: 0.72,
  },

  cardMediaInner: {
    position: "absolute",
    inset: 0,
    padding: "10px 10px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  cardImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center center",
    display: "block",
    filter: "drop-shadow(0 12px 24px rgba(43,49,66,0.18))",
  },

  cardAvatar: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  cardInitials: {
    fontSize: 58,
    color: "rgba(255,255,255,0.92)",
    fontWeight: 950,
    letterSpacing: -1,
    userSelect: "none",
  },

  photoOverlay: {
    position: "absolute",
    inset: "10px 10px auto 10px",
    display: "flex",
    justifyContent: "space-between",
    pointerEvents: "none",
  },

  profileNo: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 950,
  },

  tierOverlay: {
    padding: "5px 9px",
    borderRadius: 999,
    background: "rgba(230,62,92,0.92)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 950,
    textTransform: "capitalize",
  },

  cardBody: { padding: "18px 18px 20px" },
  cardTitleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 8,
  },

  cardName: {
    fontSize: 18,
    fontWeight: 950,
    color: C.text,
    margin: 0,
    letterSpacing: -0.4,
    flex: 1,
    minWidth: 0,
  },
  selectionCheckbox: {
    width: 24,
    height: 24,
    accentColor: C.accent,
    cursor: "pointer",
    flexShrink: 0,
    marginTop: 2,
  },

  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px 16px",
    marginBottom: 10,
  },

  metaItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.2,
  },

  metaIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    flexShrink: 0,
  },

  pillRow: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 },

  pill: {
    fontSize: 11,
    background: "#fff6f6",
    color: C.accentDark,
    padding: "5px 10px",
    borderRadius: 999,
    border: "1px solid rgba(230,62,92,0.12)",
    fontWeight: 850,
  },

  locationMeta: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    margin: "0 0 10px",
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.35,
  },

  infoStack: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginBottom: 12,
    color: C.text,
    fontSize: 13,
    fontWeight: 800,
  },

  cardDetailList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginBottom: 12,
  },

  cardDetailItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.45,
  },

  cardDetailText: {
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.45,
  },

  cardDetailTextSingleLine: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    flex: 1,
  },

  cardAbout: {
    fontSize: 13,
    color: C.muted,
    lineHeight: 1.62,
    margin: "0 0 16px",
  },

  viewBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: 42,
    padding: "0 18px",
    background: "#fff",
    border: `1.5px solid ${C.accent}`,
    color: C.accent,
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 950,
    transition: "all .15s",
    whiteSpace: "nowrap",
  },

  viewBtnHover: {
    background: C.accent,
    color: "#fff",
  },
  viewBtnActive: {
    background: C.accent,
    color: "#fff",
    boxShadow: "0 12px 26px rgba(230,62,92,0.18)",
  },

  cardActionRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 10,
  },

  cardActionRowSplit: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },

  secondaryActionBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: 42,
    padding: "0 18px",
    background: "#fff7f5",
    border: "1.5px solid rgba(215,60,46,0.18)",
    color: C.text,
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    transition: "all .15s",
    whiteSpace: "nowrap",
  },

  listCard: {
    display: "grid",
    gridTemplateColumns: "120px 1fr auto",
    gap: 18,
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid rgba(230,62,92,0.10)",
    borderRadius: 24,
    padding: 16,
    boxShadow: "0 16px 42px rgba(230,62,92,0.08)",
    transition: "transform .2s ease, box-shadow .2s ease",
    animation: "fadeUp 0.28s ease both",
  },

  listCardHover: {
    transform: "translateY(-3px)",
    boxShadow: "0 20px 50px rgba(230,62,92,0.12)",
  },
  listCardSelected: {
    border: "1px solid rgba(215,60,46,0.32)",
    boxShadow: "0 20px 50px rgba(230,62,92,0.14)",
  },

  listPhotoWrap: {
    width: 116,
    height: 116,
    borderRadius: 20,
    overflow: "hidden",
    position: "relative",
    background: "linear-gradient(180deg, rgba(248,233,235,0.95), rgba(255,255,255,0.98))",
  },

  listMediaInner: {
    position: "absolute",
    inset: 0,
    padding: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  listImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center center",
    display: "block",
    filter: "drop-shadow(0 10px 22px rgba(43,49,66,0.18))",
  },

  listAvatar: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
  },

  listInitials: {
    fontSize: 38,
    color: "rgba(255,255,255,0.92)",
    fontWeight: 950,
  },

  listBody: { minWidth: 0 },
  listTop: { display: "flex", justifyContent: "space-between", gap: 12 },
  listTopMain: { minWidth: 0, flex: 1 },
  listTopAside: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  listName: { margin: 0, color: C.text, fontSize: 20, fontWeight: 950 },
  listMeta: { margin: "5px 0 10px", color: C.muted, fontSize: 13, fontWeight: 800 },

  tierBadge: {
    height: 26,
    padding: "0 10px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 11,
    fontWeight: 950,
    textTransform: "capitalize",
  },
};
